import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2, Plus, Trash2, Upload, CheckCircle2, ShieldCheck, Clock, AlertTriangle, RefreshCw, Landmark } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import api from '../../utils/api';
import { deriveActivationState } from '../../utils/pspActivationState';
import {
  groupRequirements,
  profileEditPath,
  readinessItemSection,
  requirementFieldTestId,
  RETURN_TARGETS,
} from '../../utils/providerOnboardingGroups';
// TREATX-UAT-RELEASE-FIX-SEQUENCE-01 / Phase 5 — display helper that
// normalises a stored country value (ISO-2 code OR legacy display name)
// to a consistent human-readable label.
import { displayCountry } from '../../utils/countryNormalize';
import { SettlementAccountsCard } from '../../components/clinic/SettlementAccountsCard';
// TREATX-UAT-PHONE-BULK-MIGRATION-02 — canonical E.164 phone input.
import { PhoneInput } from '../../components/ui/phone-input';

// ---------------------------------------------------------------------------
// Provider Profile — SSOT-clean surface
//
// This page is the single source of truth for provider operational identity
// and must contain ONLY the four blocks below:
//   1. Provider Details (read-only, hydrated from providers → provider_applications)
//   2. Contact & Operations (editable — PUT /api/clinic/profile)
//   3. Billing & Legal Information (editable — PUT /api/clinic/billing)
//   4. Bank Accounts (CRUD — /api/clinic/bank-accounts)
//
// Removed in this phase (with their imports, handlers, state and API calls):
//   - About (and its languages/accreditations/vip/founding-year/etc. inputs)
//   - Social Links
//   - Treatment Showcase (duplicate of the Treatment module — SSOT violation)
//   - Differentiate Your Clinic (TreatX Admin owns positioning tags — SSOT)
//   - Visibility & Compliance
//   - The bottom "Save Profile" button that called POST /clinic/profile/enrich
//   - The B/A image upload hitting POST /clinic/documents/upload
//   - The moderation-badge + TR_STRICT banner tied to the legacy profile_public
// ---------------------------------------------------------------------------

// ===========================================================================
// 1. Provider Details (READ-ONLY)
// ===========================================================================
function IdentityCard({ identity }) {
  const { t } = useTranslation();
  const brand = (identity.brand_name || '').trim();
  const address = (identity.address || '').trim();
  const city = (identity.city || '').trim();
  const country = displayCountry(identity.country || '');
  const email = (identity.email || '').trim();
  const phone = (identity.phone || '').trim();

  const formattedAddress = [address, [city, country].filter(Boolean).join(', ')]
    .filter(Boolean)
    .join(' — ');

  const Placeholder = ({ testId }) => (
    <span className="text-sm text-slate-400 italic" data-testid={testId}>{t('provider.profile.identity.notProvided')}</span>
  );

  return (
    <Card
      className="border border-slate-200/60 shadow-sm"
      data-testid="clinic-identity-card"
    >
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3
            className="text-sm font-semibold text-[#122033] font-[Manrope]"
            data-testid="clinic-identity-title"
          >
            {t('provider.profile.identity.title')}
          </h3>
          <Badge
            className="text-[10px] px-1.5 py-0 bg-slate-100 text-slate-600 border border-slate-200"
            data-testid="clinic-identity-readonly-badge"
          >
            {t('provider.profile.identity.readOnlyBadge')}
          </Badge>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label={t('provider.profile.identity.brandName')} testId="identity-brand-name">
            {brand
              ? <span data-testid="identity-brand-name-value">{brand}</span>
              : <Placeholder testId="identity-brand-name-placeholder" />}
          </Field>

          <Field label={t('provider.profile.identity.email')} testId="identity-email">
            {email
              ? <span data-testid="identity-email-value">{email}</span>
              : <Placeholder testId="identity-email-placeholder" />}
          </Field>

          <Field label={t('provider.profile.identity.phone')} testId="identity-phone">
            {phone
              ? <span data-testid="identity-phone-value">{phone}</span>
              : <Placeholder testId="identity-phone-placeholder" />}
          </Field>

          <Field label={t('provider.profile.identity.cityCountry')} testId="identity-city-country">
            {(city || country)
              ? (
                <span data-testid="identity-city-country-value">
                  {[city, country].filter(Boolean).join(', ')}
                </span>
              )
              : <Placeholder testId="identity-city-country-placeholder" />}
          </Field>

          <div className="sm:col-span-2">
            <Field label={t('provider.profile.identity.address')} testId="identity-address">
              {formattedAddress
                ? <span data-testid="identity-address-value">{formattedAddress}</span>
                : <Placeholder testId="identity-address-placeholder" />}
            </Field>
          </div>
        </div>

        <p
          className="text-[11px] text-slate-400 pt-1"
          data-testid="clinic-identity-source-note"
        >
          {t('provider.profile.identity.sourceNote')}
        </p>
      </CardContent>
    </Card>
  );
}

function Field({ label, testId, children }) {
  return (
    <div data-testid={`${testId}-field`}>
      <Label
        className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block"
        data-testid={`${testId}-label`}
      >
        {label}
      </Label>
      <div className="text-sm text-[#122033]">{children}</div>
    </div>
  );
}

// ===========================================================================
// 2. Contact & Operations (EDITABLE — PUT /api/clinic/profile)
// ===========================================================================
function ContactOperationsCard({ draft, onChange, onSave, saving, dirty }) {
  const { t } = useTranslation();
  return (
    <Card
      className="border border-slate-200/60 shadow-sm"
      data-testid="clinic-contact-ops-card"
    >
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3
            className="text-sm font-semibold text-[#122033] font-[Manrope]"
            data-testid="clinic-contact-ops-title"
          >
            {t('provider.profile.contact.title')}
          </h3>
          <Button
            size="sm"
            onClick={onSave}
            disabled={!dirty || saving}
            className="h-8 text-xs"
            data-testid="contact-ops-save"
          >
            {saving
              ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              : <Save className="h-3.5 w-3.5 mr-1" />}
            {saving ? t('provider.profile.contact.saving') : t('provider.profile.contact.save')}
          </Button>
        </div>
        <p
          className="text-[11px] text-slate-500"
          data-testid="clinic-contact-ops-helper"
        >
          {t('provider.profile.contact.helper')}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div data-testid="contact-public-email-field">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.profile.contact.publicEmail')}</Label>
            <Input
              type="email"
              value={draft.public_contact_email || ''}
              onChange={(e) => onChange('public_contact_email', e.target.value)}
              placeholder={t('provider.profile.contact.publicEmailPlaceholder')}
              className="text-sm"
              data-testid="contact-public-email-input"
            />
          </div>

          <div data-testid="contact-public-phone-field">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.profile.contact.publicPhone')}</Label>
            <PhoneInput
              value={draft.public_contact_phone || ''}
              onChange={(e164) => onChange('public_contact_phone', e164)}
              testId="contact-public-phone-input"
              placeholder={t('provider.profile.contact.phoneNumberPlaceholder')}
            />
          </div>

          <div className="sm:col-span-2" data-testid="contact-address-field">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.profile.contact.addressLabel')}</Label>
            <textarea
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-y min-h-[64px] focus:outline-none focus:ring-1 focus:ring-slate-300"
              value={draft.address || ''}
              onChange={(e) => onChange('address', e.target.value)}
              placeholder={t('provider.profile.contact.addressPlaceholder')}
              maxLength={500}
              data-testid="contact-address-input"
            />
          </div>

          <div className="sm:col-span-2" data-testid="contact-opening-hours-field">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.profile.contact.openingHours')}</Label>
            <textarea
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-y min-h-[64px] focus:outline-none focus:ring-1 focus:ring-slate-300"
              value={draft.opening_hours || ''}
              onChange={(e) => onChange('opening_hours', e.target.value)}
              placeholder={t('provider.profile.contact.openingHoursPlaceholder')}
              maxLength={500}
              data-testid="contact-opening-hours-input"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ===========================================================================
// 3. Billing & Legal Information (EDITABLE — PUT /api/clinic/billing)
// ===========================================================================
function BillingCard({ draft, onChange, onSave, saving, dirty, currentBrand, currentLegal, amendment, onRequestChange }) {
  const { t } = useTranslation();
  const pending = amendment?.status === 'pending';
  const rejected = amendment?.status === 'rejected';
  const notProvided = t('provider.profile.identity.notProvided');
  return (
    <Card
      className="border border-slate-200/60 shadow-sm"
      data-testid="billing-legal-card"
    >
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3
            className="text-sm font-semibold text-[#122033] font-[Manrope]"
            data-testid="billing-legal-title"
          >
            {t('provider.profile.billing.title')}
          </h3>
          <Button
            size="sm"
            onClick={onSave}
            disabled={!dirty || saving}
            className="h-8 text-xs"
            data-testid="billing-save"
          >
            {saving
              ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              : <Save className="h-3.5 w-3.5 mr-1" />}
            {saving ? t('provider.profile.billing.saving') : t('provider.profile.billing.save')}
          </Button>
        </div>
        <p className="text-[11px] text-slate-500" data-testid="billing-legal-helper">
          {t('provider.profile.billing.helper')}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* PROVIDER-IDENTITY-AMENDMENT (3B) — Public Brand + Registered
              Legal Entity Name are the Provider's approved identity. They are
              NOT ordinary editable fields: changes require a TreatX-reviewed
              amendment request (3A authority). Direct writes via
              PUT /clinic/billing are blocked server-side. */}
          <div
            className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50/70 p-4 space-y-4"
            data-testid="identity-approved-block"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-slate-500" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  {t('provider.profile.billing.identity.sectionTitle')}
                </span>
              </div>
              {pending ? (
                <Badge
                  variant="outline"
                  className="border-amber-300 bg-amber-50 text-amber-700 text-[11px] font-medium gap-1"
                  data-testid="identity-pending-badge"
                >
                  <Clock className="h-3 w-3" />
                  {t('provider.profile.billing.identity.pendingStatus')}
                </Badge>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onRequestChange}
                  className="h-8 text-xs"
                  data-testid="identity-request-change-btn"
                >
                  {t('provider.profile.billing.identity.requestChange')}
                </Button>
              )}
            </div>

            {/* Public Brand / display name */}
            <div data-testid="identity-brand-row">
              <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">
                {t('provider.profile.billing.brandName')}
              </Label>
              <p className="text-sm font-medium text-slate-800" data-testid="identity-current-brand">
                {(currentBrand || '').trim() || <span className="text-slate-400 italic font-normal">{notProvided}</span>}
              </p>
              {pending && amendment?.proposed_brand_name && (
                <p className="text-[12px] text-amber-700 mt-1" data-testid="identity-requested-brand">
                  {t('provider.profile.billing.identity.requestedLabel')}: <span className="font-medium">{amendment.proposed_brand_name}</span>
                </p>
              )}
              <p className="text-[11px] text-slate-500 mt-1">{t('provider.profile.billing.brandNameHelper')}</p>
            </div>

            {/* Registered legal entity name */}
            <div data-testid="identity-legal-row">
              <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">
                {t('provider.profile.billing.legalName')}
              </Label>
              <p className="text-sm font-medium text-slate-800" data-testid="identity-current-legal">
                {(currentLegal || '').trim() || <span className="text-slate-400 italic font-normal">{notProvided}</span>}
              </p>
              {pending && amendment?.proposed_invoice_legal_entity_name && (
                <p className="text-[12px] text-amber-700 mt-1" data-testid="identity-requested-legal">
                  {t('provider.profile.billing.identity.requestedLabel')}: <span className="font-medium">{amendment.proposed_invoice_legal_entity_name}</span>
                </p>
              )}
              <p className="text-[11px] text-slate-500 mt-1">{t('provider.profile.billing.legalNameHelper')}</p>
            </div>

            {pending && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="identity-pending-helper">
                {t('provider.profile.billing.identity.pendingHelper')}
              </p>
            )}
            {!pending && rejected && (
              <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 space-y-1" data-testid="identity-rejected-note">
                <p>{t('provider.profile.billing.identity.rejectedNote')}</p>
                {amendment?.rejection_reason && (
                  <p data-testid="identity-rejected-reason">
                    <span className="font-semibold">{t('provider.profile.billing.identity.reasonLabel')}:</span> {amendment.rejection_reason}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="sm:col-span-2" data-testid="billing-address-field">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.profile.billing.billingAddress')}</Label>
            <textarea
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-y min-h-[64px] focus:outline-none focus:ring-1 focus:ring-slate-300"
              value={draft.billing_address || ''}
              onChange={(e) => onChange('billing_address', e.target.value)}
              placeholder={t('provider.profile.billing.billingAddressPlaceholder')}
              maxLength={500}
              data-testid="billing-address-input"
            />
          </div>

          <div data-testid="billing-tax-id-field">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.profile.billing.taxId')}</Label>
            <Input
              value={draft.tax_id || ''}
              onChange={(e) => onChange('tax_id', e.target.value)}
              placeholder={t('provider.profile.billing.taxIdPlaceholder')}
              className="text-sm"
              data-testid="billing-tax-id-input"
              maxLength={80}
            />
          </div>

          {/* PSP sub-merchant KYC. The registered legal company name is ONE
              fact: while the provider has no approved value it is editable
              here; once approved it is locked and changes go through the
              identity-amendment flow (server-enforced). */}
          <div className="sm:col-span-2" data-testid="billing-legal-name-field">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">
              {t('provider.profile.billing.legalCompanyName.label')}
            </Label>
            {draft.legal_name_locked ? (
              <p className="text-sm font-medium text-slate-800" data-testid="billing-legal-name-locked">
                {(draft.legal_name || '').trim() || <span className="text-slate-400 italic font-normal">{notProvided}</span>}
              </p>
            ) : (
              <Input
                value={draft.legal_name || ''}
                onChange={(e) => onChange('legal_name', e.target.value)}
                placeholder={t('provider.profile.billing.legalCompanyName.placeholder')}
                className="text-sm"
                data-testid="billing-legal-name-input"
                maxLength={200}
              />
            )}
            <p className="text-[11px] text-slate-500 mt-1">
              {draft.legal_name_locked
                ? t('provider.profile.billing.legalCompanyName.lockedHelper')
                : t('provider.profile.billing.legalCompanyName.helper')}
            </p>
          </div>

          <div data-testid="billing-contact-name-field">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">
              {t('provider.profile.billing.contactName.label')}
            </Label>
            <Input
              value={draft.contact_name || ''}
              onChange={(e) => onChange('contact_name', e.target.value)}
              placeholder={t('provider.profile.billing.contactName.placeholder')}
              className="text-sm"
              data-testid="billing-contact-name-input"
              maxLength={100}
            />
            <p className="text-[11px] text-slate-500 mt-1">
              {t('provider.profile.billing.contactName.helper')}
            </p>
          </div>

          <div data-testid="billing-contact-surname-field">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">
              {t('provider.profile.billing.contactSurname.label')}
            </Label>
            <Input
              value={draft.contact_surname || ''}
              onChange={(e) => onChange('contact_surname', e.target.value)}
              placeholder={t('provider.profile.billing.contactSurname.placeholder')}
              className="text-sm"
              data-testid="billing-contact-surname-input"
              maxLength={100}
            />
          </div>

          {/* Tax office applies to registered companies only — an individual
              practitioner has none. The PROVIDER's own tax office, never
              TreatX's. */}
          {String(draft.provider_type || '').toLowerCase() !== 'doctor_practice' && (
            <div className="sm:col-span-2" data-testid="billing-tax-office-field">
              <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">
                {t('provider.profile.billing.taxOffice.label')}
              </Label>
              <Input
                value={draft.tax_office || ''}
                onChange={(e) => onChange('tax_office', e.target.value)}
                placeholder={t('provider.profile.billing.taxOffice.placeholder')}
                className="text-sm"
                data-testid="billing-tax-office-input"
                maxLength={150}
              />
              <p className="text-[11px] text-slate-500 mt-1">
                {t('provider.profile.billing.taxOffice.helper')}
              </p>
            </div>
          )}

          {/* F9: personal national identity number — only relevant for
              individual providers (doctor_practice). Kept semantically
              distinct from `tax_id` (corporate/VAT number) to avoid
              collapsing two business facts onto one canonical field. */}
          {String(draft.provider_type || '').toLowerCase() === 'doctor_practice' && (
            <div className="sm:col-span-2" data-testid="billing-personal-id-field">
              <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">
                {t('provider.profile.billing.personalId.label')}
              </Label>
              <Input
                value={draft.personal_identity_number || ''}
                onChange={(e) => onChange('personal_identity_number', e.target.value)}
                placeholder={t('provider.profile.billing.personalId.placeholder')}
                className="text-sm"
                data-testid="billing-personal-id-input"
                maxLength={80}
              />
              <p className="text-[11px] text-slate-500 mt-1">
                {t('provider.profile.billing.personalId.helper')}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ===========================================================================
// 3b. Identity Amendment request dialog (PROVIDER-IDENTITY-AMENDMENT 3B)
// Provider-side request-entry surface for changing the approved Public Brand
// and/or Registered Legal Entity Name. Submits through the 3A amendment API
// (POST /api/provider/identity-amendments). Never writes approved identity
// directly.
// ===========================================================================
function IdentityAmendmentDialog({ open, onOpenChange, currentBrand, currentLegal, onSubmit, submitting }) {
  const { t } = useTranslation();
  const curBrand = (currentBrand || '').trim();
  const curLegal = (currentLegal || '').trim();
  const [brand, setBrand] = useState(curBrand);
  const [legal, setLegal] = useState(curLegal);
  const [error, setError] = useState('');

  // Reset the draft to the current approved values every time the dialog opens
  // so the Provider edits only what is changing (no auto-copy legal→brand).
  useEffect(() => {
    if (open) {
      setBrand(curBrand);
      setLegal(curLegal);
      setError('');
    }
  }, [open, curBrand, curLegal]);

  const handleSubmit = () => {
    const nb = brand.trim();
    const nl = legal.trim();
    if (!nb && !nl) { setError(t('provider.profile.billing.identity.validation.bothBlank')); return; }
    if ((curBrand && !nb) || (curLegal && !nl)) { setError(t('provider.profile.billing.identity.validation.blankReplacement')); return; }
    const brandChanged = nb !== curBrand;
    const legalChanged = nl !== curLegal;
    if (!brandChanged && !legalChanged) { setError(t('provider.profile.billing.identity.validation.noChange')); return; }
    const payload = {};
    if (brandChanged) payload.brand_name = nb;
    if (legalChanged) payload.invoice_legal_entity_name = nl;
    setError('');
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="identity-amendment-dialog">
        <DialogHeader>
          <DialogTitle data-testid="identity-amendment-dialog-title">
            {t('provider.profile.billing.identity.dialog.title')}
          </DialogTitle>
          <DialogDescription data-testid="identity-amendment-dialog-desc">
            {t('provider.profile.billing.identity.dialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div data-testid="identity-amendment-brand-field">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">
              {t('provider.profile.billing.brandName')}
            </Label>
            <Input
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              placeholder={t('provider.profile.billing.brandNamePlaceholder')}
              className="text-sm"
              data-testid="identity-amendment-brand-input"
              maxLength={200}
            />
            {curBrand && (
              <p className="text-[11px] text-slate-400 mt-1">
                {t('provider.profile.billing.identity.dialog.currentPrefix', { value: curBrand })}
              </p>
            )}
          </div>

          <div data-testid="identity-amendment-legal-field">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">
              {t('provider.profile.billing.legalName')}
            </Label>
            <Input
              value={legal}
              onChange={(e) => setLegal(e.target.value)}
              placeholder={t('provider.profile.billing.legalNamePlaceholder')}
              className="text-sm"
              data-testid="identity-amendment-legal-input"
              maxLength={300}
            />
            {curLegal && (
              <p className="text-[11px] text-slate-400 mt-1">
                {t('provider.profile.billing.identity.dialog.currentPrefix', { value: curLegal })}
              </p>
            )}
          </div>

          {error && (
            <p className="text-[12px] text-rose-600" data-testid="identity-amendment-error">{error}</p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="h-9 text-sm"
            data-testid="identity-amendment-cancel"
          >
            {t('provider.profile.billing.identity.dialog.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="h-9 text-sm"
            data-testid="identity-amendment-submit"
          >
            {submitting
              ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              : null}
            {submitting
              ? t('provider.profile.billing.identity.dialog.submitting')
              : t('provider.profile.billing.identity.dialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ===========================================================================
// 4. Bank Accounts (CRUD — /api/clinic/bank-accounts)
// ===========================================================================
const EMPTY_BANK_FORM = {
  beneficiary_name: '',
  beneficiary_address: '',
  bank_name: '',
  bank_address: '',
  currency: '',
  iban: '',
  account_number: '',
  swift_bic: '',
  intermediary_bank_name: '',
  intermediary_swift_bic: '',
  is_primary: false,
};

// Phase P4 · Step 2 + Step 4 — Bank Account Validation Hardening.
// Strict allowlist + regex validators mirror the backend in
// /app/backend/routes/clinic_profile_enrichment_routes.py.
// Step 4 — expanded currency list for international payouts.
const ALLOWED_CURRENCIES = [
  'EUR', 'USD', 'TRY', 'GBP', 'HUF', 'PLN', 'CZK', 'INR', 'KRW',
  'AED', 'SAR', 'QAR', 'KWD', 'BHD', 'THB', 'IDR', 'VND',
  'ZAR', 'NGN', 'EGP', 'MXN', 'BRL',
];
const SWIFT_REGEX = /^[A-Z0-9]{8}(?:[A-Z0-9]{3})?$/;
const IBAN_STRUCTURE_REGEX = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}$/;
const normalizeIban = (v) => (v || '').replace(/\s+/g, '').toUpperCase();

// Step 4 — structure-only IBAN validator (mirrors backend `_is_valid_iban`).
// Rules: after normalization, match /^[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}$/
// AND total length between 15 and 34. No checksum, no country rules.
function isValidIban(raw) {
  const iban = normalizeIban(raw);
  if (!IBAN_STRUCTURE_REGEX.test(iban)) return false;
  if (iban.length < 15 || iban.length > 34) return false;
  return true;
}

function validateBankForm(form, t) {
  const errors = {};
  const currency = (form.currency || '').trim().toUpperCase();
  if (!currency) errors.currency = t('provider.profile.bank.errors.currencyRequired');
  else if (!ALLOWED_CURRENCIES.includes(currency)) errors.currency = t('provider.profile.bank.errors.currencyUnsupported');

  const swift = (form.swift_bic || '').trim().toUpperCase();
  if (!swift) errors.swift_bic = t('provider.profile.bank.errors.swiftRequired');
  else if (!SWIFT_REGEX.test(swift)) errors.swift_bic = t('provider.profile.bank.errors.swiftInvalid');

  const iban = normalizeIban(form.iban);
  const acct = (form.account_number || '').trim();
  if (!iban && !acct) errors._identifier = t('provider.profile.bank.errors.identifierRequired');
  if (iban && !isValidIban(iban)) errors.iban = t('provider.profile.bank.errors.ibanInvalid');

  const interSwift = (form.intermediary_swift_bic || '').trim().toUpperCase();
  if (interSwift && !SWIFT_REGEX.test(interSwift)) errors.intermediary_swift_bic = t('provider.profile.bank.errors.intermediarySwiftInvalid');

  return errors;
}

function BankAccountsCard({ accounts, onCreate, onUpdate, onDelete, onSetPrimary }) {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_BANK_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const resetForm = () => { setForm(EMPTY_BANK_FORM); setFormErrors({}); setEditingId(null); setAdding(false); };
  const startAdd = () => { setForm(EMPTY_BANK_FORM); setFormErrors({}); setEditingId(null); setAdding(true); };
  const startEdit = (a) => {
    setForm({
      beneficiary_name: a.beneficiary_name || '',
      beneficiary_address: a.beneficiary_address || '',
      bank_name: a.bank_name || '',
      bank_address: a.bank_address || '',
      currency: a.currency || '',
      iban: a.iban || '',
      account_number: a.account_number || '',
      swift_bic: a.swift_bic || '',
      intermediary_bank_name: a.intermediary_bank_name || '',
      intermediary_swift_bic: a.intermediary_swift_bic || '',
      is_primary: !!a.is_primary,
    });
    setFormErrors({});
    setEditingId(a.id);
    setAdding(false);
  };

  const submit = async () => {
    const errs = validateBankForm(form, t);
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) {
      const first = errs.currency || errs.swift_bic || errs._identifier || errs.iban || errs.intermediary_swift_bic;
      if (first) toast.error(first);
      return;
    }
    setBusy(true);
    try {
      // Submit with normalized IBAN (no spaces) so the backend stores the
      // canonical form. The backend also strips/uppercases defensively.
      const payload = { ...form, iban: normalizeIban(form.iban) };
      if (editingId) {
        await onUpdate(editingId, payload);
      } else {
        await onCreate(payload);
      }
      resetForm();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || t('provider.profile.bank.errors.saveFailed', { message: e.message }));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (id) => {
    setBusy(true);
    try { await onDelete(id); }
    catch (e) { toast.error(e?.response?.data?.detail || t('provider.profile.bank.errors.deleteFailed', { message: e.message })); }
    finally { setBusy(false); }
  };

  const doSetPrimary = async (id) => {
    setBusy(true);
    try { await onSetPrimary(id); }
    catch (e) { toast.error(e?.response?.data?.detail || t('provider.profile.bank.errors.setPrimaryFailed', { message: e.message })); }
    finally { setBusy(false); }
  };

  const editing = adding || !!editingId;

  return (
    <Card className="border border-slate-200/60 shadow-sm" data-testid="bank-accounts-card">
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#122033] font-[Manrope]" data-testid="bank-accounts-title">
            {t('provider.profile.bank.title')}
          </h3>
          {!editing && (
            <Button size="sm" onClick={startAdd} className="h-8 text-xs" data-testid="bank-add-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> {t('provider.profile.bank.add')}
            </Button>
          )}
        </div>
        <p className="text-[11px] text-slate-500">
          {t('provider.profile.bank.helper')}
        </p>

        {accounts.length === 0 && !editing && (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="bank-empty-state">
            {t('provider.profile.bank.empty')}
          </p>
        )}

        {!editing && accounts.map(a => (
          <div
            key={a.id}
            className="border border-slate-200 rounded-md p-3 space-y-1"
            data-testid={`bank-account-${a.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#122033] flex items-center gap-2" data-testid={`bank-bank-name-${a.id}`}>
                  {a.bank_name || t('provider.profile.bank.unnamedBank')}
                  <Badge
                    className="text-[10px] bg-slate-100 text-slate-700 border border-slate-200"
                    data-testid={`bank-currency-${a.id}`}
                  >
                    {a.currency}
                  </Badge>
                  {a.is_primary && (
                    <Badge
                      className="text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-200"
                      data-testid={`bank-primary-badge-${a.id}`}
                    >
                      {t('provider.profile.bank.primaryBadge')}
                    </Badge>
                  )}
                </p>
                <p className="text-xs text-slate-500" data-testid={`bank-beneficiary-${a.id}`}>
                  {a.beneficiary_name || '—'}
                </p>
                <p className="text-[11px] text-slate-400 font-mono truncate" data-testid={`bank-ibanacct-${a.id}`}>
                  {a.iban ? `${t('provider.profile.bank.ibanPrefix')} ${a.iban}` : (a.account_number ? `${t('provider.profile.bank.accountPrefix')} ${a.account_number}` : '')}
                  {a.swift_bic ? ` · ${t('provider.profile.bank.swiftPrefix')} ${a.swift_bic}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {!a.is_primary && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-[11px]"
                    onClick={() => doSetPrimary(a.id)}
                    disabled={busy}
                    data-testid={`bank-set-primary-${a.id}`}
                  >
                    {t('provider.profile.bank.setPrimary')}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => startEdit(a)}
                  disabled={busy}
                  data-testid={`bank-edit-${a.id}`}
                >
                  <Upload className="h-3.5 w-3.5 rotate-180 opacity-60" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-red-500"
                  onClick={() => doDelete(a.id)}
                  disabled={busy}
                  data-testid={`bank-delete-${a.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}

        {editing && (
          <div className="border border-blue-200 rounded-md p-3 space-y-2 bg-blue-50/30" data-testid="bank-form">
            <p className="text-xs font-semibold text-[#122033]">
              {editingId ? t('provider.profile.bank.form.editTitle') : t('provider.profile.bank.form.newTitle')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <BankField label={t('provider.profile.bank.form.beneficiaryName')} value={form.beneficiary_name}
                onChange={(v) => setForm(f => ({ ...f, beneficiary_name: v }))} tid="bank-form-beneficiary-name" />
              <BankField label={t('provider.profile.bank.form.beneficiaryAddress')} value={form.beneficiary_address}
                onChange={(v) => setForm(f => ({ ...f, beneficiary_address: v }))} tid="bank-form-beneficiary-address" />
              <BankField label={t('provider.profile.bank.form.bankName')} value={form.bank_name}
                onChange={(v) => setForm(f => ({ ...f, bank_name: v }))} tid="bank-form-bank-name" />
              <BankField label={t('provider.profile.bank.form.bankAddress')} value={form.bank_address}
                onChange={(v) => setForm(f => ({ ...f, bank_address: v }))} tid="bank-form-bank-address" />

              {/* Currency — strict allowlist dropdown (EUR / USD / TRY). */}
              <div>
                <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">
                  {t('provider.profile.bank.form.currency')}<span className="text-red-500"> *</span>
                </Label>
                <select
                  value={form.currency || ''}
                  onChange={(e) => { setForm(f => ({ ...f, currency: e.target.value })); setFormErrors(prev => ({ ...prev, currency: undefined })); }}
                  className={`w-full h-9 border rounded-md px-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-slate-300 ${formErrors.currency ? 'border-red-400' : 'border-slate-200'}`}
                  data-testid="bank-form-currency"
                >
                  <option value="">{t('provider.profile.bank.form.selectCurrency')}</option>
                  {ALLOWED_CURRENCIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                {formErrors.currency && (
                  <p className="text-[11px] text-red-600 mt-1" data-testid="bank-form-currency-error">{formErrors.currency}</p>
                )}
              </div>

              {/* SWIFT/BIC — regex validated (8 or 11 uppercase alphanumeric). */}
              <div>
                <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">
                  {t('provider.profile.bank.form.swiftBic')}<span className="text-red-500"> *</span>
                </Label>
                <Input
                  value={form.swift_bic || ''}
                  onChange={(e) => { setForm(f => ({ ...f, swift_bic: e.target.value.toUpperCase() })); setFormErrors(prev => ({ ...prev, swift_bic: undefined })); }}
                  className={`text-sm h-9 ${formErrors.swift_bic ? 'border-red-400 focus:ring-red-300' : ''}`}
                  data-testid="bank-form-swift"
                  maxLength={11}
                />
                {formErrors.swift_bic && (
                  <p className="text-[11px] text-red-600 mt-1" data-testid="bank-form-swift-error">{formErrors.swift_bic}</p>
                )}
              </div>

              {/* IBAN — country-code + alphanumeric 15–34. */}
              <div>
                <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.profile.bank.form.iban')}</Label>
                <Input
                  value={form.iban || ''}
                  onChange={(e) => { setForm(f => ({ ...f, iban: e.target.value.toUpperCase() })); setFormErrors(prev => ({ ...prev, iban: undefined, _identifier: undefined })); }}
                  className={`text-sm h-9 font-mono ${formErrors.iban ? 'border-red-400 focus:ring-red-300' : ''}`}
                  data-testid="bank-form-iban"
                />
                {formErrors.iban && (
                  <p className="text-[11px] text-red-600 mt-1" data-testid="bank-form-iban-error">{formErrors.iban}</p>
                )}
              </div>

              <div>
                <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.profile.bank.form.accountNumber')}</Label>
                <Input
                  value={form.account_number || ''}
                  onChange={(e) => { setForm(f => ({ ...f, account_number: e.target.value })); setFormErrors(prev => ({ ...prev, _identifier: undefined })); }}
                  className="text-sm h-9"
                  data-testid="bank-form-account-number"
                />
              </div>

              <BankField label={t('provider.profile.bank.form.intermediaryBankName')} value={form.intermediary_bank_name}
                onChange={(v) => setForm(f => ({ ...f, intermediary_bank_name: v }))} tid="bank-form-intermediary-name" />

              <div>
                <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.profile.bank.form.intermediarySwift')}</Label>
                <Input
                  value={form.intermediary_swift_bic || ''}
                  onChange={(e) => { setForm(f => ({ ...f, intermediary_swift_bic: e.target.value.toUpperCase() })); setFormErrors(prev => ({ ...prev, intermediary_swift_bic: undefined })); }}
                  className={`text-sm h-9 ${formErrors.intermediary_swift_bic ? 'border-red-400 focus:ring-red-300' : ''}`}
                  data-testid="bank-form-intermediary-swift"
                  maxLength={11}
                />
                {formErrors.intermediary_swift_bic && (
                  <p className="text-[11px] text-red-600 mt-1" data-testid="bank-form-intermediary-swift-error">{formErrors.intermediary_swift_bic}</p>
                )}
              </div>
            </div>
            {formErrors._identifier && (
              <p className="text-[11px] text-red-600" data-testid="bank-form-identifier-error">{formErrors._identifier}</p>
            )}
            <label className="flex items-center gap-2 text-xs text-[#122033]" data-testid="bank-form-primary-wrapper">
              <input
                type="checkbox"
                checked={!!form.is_primary}
                onChange={(e) => setForm(f => ({ ...f, is_primary: e.target.checked }))}
                data-testid="bank-form-is-primary"
              />
              {t('provider.profile.bank.form.setPrimaryCheckbox')}
            </label>
            <p className="text-[11px] text-slate-500">
              {t('provider.profile.bank.form.identifierHelper')}
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={submit} disabled={busy} data-testid="bank-form-submit">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                {editingId ? t('provider.profile.bank.form.update') : t('provider.profile.bank.form.addAccount')}
              </Button>
              <Button size="sm" variant="ghost" onClick={resetForm} disabled={busy} data-testid="bank-form-cancel">
                {t('provider.profile.bank.form.cancel')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BankField({ label, value, onChange, required, tid }) {
  return (
    <div>
      <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">
        {label}{required && <span className="text-red-500"> *</span>}
      </Label>
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="text-sm h-9"
        data-testid={tid}
      />
    </div>
  );
}

// ===========================================================================
// 5. Online Presence (EDITABLE — reuses PUT /api/clinic/profile)
//    Minimal additive section: website + 4 social links. Basic URL check.
// ===========================================================================
function OnlinePresenceCard({ draft, errors, onChange, onSave, saving, dirty }) {
  const { t } = useTranslation();
  const FIELDS = [
    { key: 'website',   labelKey: 'provider.profile.presence.fields.website',   placeholder: 'https://yourclinic.com' },
    { key: 'instagram', labelKey: 'provider.profile.presence.fields.instagram', placeholder: 'https://instagram.com/yourclinic' },
    { key: 'youtube',   labelKey: 'provider.profile.presence.fields.youtube',   placeholder: 'https://youtube.com/@yourclinic' },
    { key: 'tiktok',    labelKey: 'provider.profile.presence.fields.tiktok',    placeholder: 'https://tiktok.com/@yourclinic' },
    { key: 'facebook',  labelKey: 'provider.profile.presence.fields.facebook',  placeholder: 'https://facebook.com/yourclinic' },
  ];
  return (
    <Card
      className="border border-slate-200/60 shadow-sm"
      data-testid="online-presence-card"
    >
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3
            className="text-sm font-semibold text-[#122033] font-[Manrope]"
            data-testid="online-presence-title"
          >
            {t('provider.profile.presence.title')}
          </h3>
          <Button
            size="sm"
            onClick={onSave}
            disabled={!dirty || saving}
            className="h-8 text-xs"
            data-testid="online-presence-save"
          >
            {saving
              ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              : <Save className="h-3.5 w-3.5 mr-1" />}
            {saving ? t('provider.profile.presence.saving') : t('provider.profile.presence.save')}
          </Button>
        </div>
        <p
          className="text-[11px] text-slate-500"
          data-testid="online-presence-helper"
        >
          {t('provider.profile.presence.helper')}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FIELDS.map(({ key, labelKey, placeholder }) => {
            const err = errors?.[key];
            return (
              <div key={key} data-testid={`online-${key}-field`}>
                <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">
                  {t(labelKey)}
                </Label>
                <Input
                  type="url"
                  value={draft[key] || ''}
                  onChange={(e) => onChange(key, e.target.value)}
                  placeholder={placeholder}
                  className={`text-sm ${err ? 'border-red-400 focus:ring-red-300' : ''}`}
                  data-testid={`online-${key}-input`}
                  maxLength={500}
                />
                {err && (
                  <p className="text-[11px] text-red-600 mt-1" data-testid={`online-${key}-error`}>{err}</p>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ===========================================================================
// 6. Positioning Tags (EDITABLE — P5-5A)
//    Multi-select from TreatX-admin-defined catalog. No free text.
// ===========================================================================
function PositioningTagsCard({ data, draft, detailsDraft, onToggle, onSetDescription, onSave, saving, dirty }) {
  const { t } = useTranslation();
  const catalog = data?.catalog || [];
  const max = data?.max || 8;
  const selected = new Set(draft || []);
  const [openSlug, setOpenSlug] = useState(null);
  return (
    <Card
      className="border border-slate-200/60 shadow-sm"
      data-testid="positioning-tags-card"
    >
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3
            className="text-sm font-semibold text-[#122033] font-[Manrope]"
            data-testid="positioning-tags-title"
          >
            {t('provider.profile.positioning.title')}
          </h3>
          <Button
            size="sm"
            onClick={onSave}
            disabled={!dirty || saving}
            className="h-8 text-xs"
            data-testid="positioning-tags-save"
          >
            {saving
              ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              : <Save className="h-3.5 w-3.5 mr-1" />}
            {saving ? t('provider.profile.positioning.saving') : t('provider.profile.positioning.save')}
          </Button>
        </div>
        <p
          className="text-[11px] text-slate-500"
          data-testid="positioning-tags-helper"
        >
          {t('provider.profile.positioning.helper', { max, count: (draft || []).length })}
        </p>

        {catalog.length === 0 ? (
          <p className="text-[12px] text-slate-400" data-testid="positioning-tags-empty">
            {t('provider.profile.positioning.empty')}
          </p>
        ) : (
          <div className="space-y-2" data-testid="positioning-tags-list">
            <div className="flex flex-wrap gap-2">
              {catalog.map(tag => {
                const isSelected = selected.has(tag.slug);
                const disabled = !isSelected && (draft || []).length >= max;
                // Localized display label with graceful fallback to backend
                // catalog `tag.display`. Canonical `tag.slug` never changes.
                const tagDisplay = t(`provider.profile.positioning.tags.${tag.slug}`, {
                  defaultValue: tag.display || tag.slug,
                });
                return (
                  <button
                    type="button"
                    key={tag.slug}
                    onClick={() => !disabled && onToggle(tag.slug)}
                    disabled={disabled}
                    data-testid={`positioning-tag-${tag.slug}`}
                    data-selected={isSelected ? 'true' : 'false'}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors ${
                      isSelected
                        ? 'bg-teal-600 border-teal-600 text-white hover:bg-teal-700'
                        : disabled
                        ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed'
                        : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {isSelected && <CheckCircle2 className="h-3 w-3" />}
                    <span>{tagDisplay}</span>
                  </button>
                );
              })}
            </div>

            {/* P7Z-2C-B: per-slug description rows for SELECTED tags */}
            {(draft || []).length > 0 && (
              <div className="space-y-1.5 pt-1" data-testid="positioning-tags-descriptions">
                {(draft || []).map((slug) => {
                  const tag = catalog.find(c => c.slug === slug);
                  const display = t(`provider.profile.positioning.tags.${slug}`, {
                    defaultValue: tag?.display || slug,
                  });
                  const value = (detailsDraft || {})[slug] || '';
                  const isOpen = openSlug === slug || !!value;
                  return (
                    <div
                      key={slug}
                      className="rounded-md border border-slate-200 px-2 py-1.5 bg-slate-50/40"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-medium text-slate-700">{display}</span>
                        {!isOpen && (
                          <button
                            type="button"
                            className="text-[11px] text-teal-600 hover:underline"
                            onClick={() => setOpenSlug(slug)}
                            data-testid={`pt-add-desc-${slug}`}
                          >
                            {t('provider.profile.positioning.addDescription')}
                          </button>
                        )}
                      </div>
                      {isOpen && (
                        <textarea
                          rows={2}
                          maxLength={240}
                          placeholder={t('provider.profile.positioning.descriptionPlaceholder')}
                          value={value}
                          onChange={(e) => onSetDescription(slug, e.target.value)}
                          className="mt-1 w-full text-[11px] p-1.5 border border-slate-200 rounded resize-none focus:outline-none focus:ring-1 focus:ring-teal-500"
                          data-testid={`pt-desc-input-${slug}`}
                        />
                      )}
                      {isOpen && (
                        <div className="text-[10px] text-slate-400 text-right">
                          {value.length}/240
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ===========================================================================
// 7. VIP Services (EDITABLE — P5-5B)
//    Multi-select (grouped by category) from TreatX-admin-defined catalog.
//    No free text. Max 15.
// ===========================================================================
function VipServicesCard({ data, draft, detailsDraft, onToggle, onSetDescription, onSave, saving, dirty }) {
  const { t } = useTranslation();
  const catalog = data?.catalog || [];
  const max = data?.max || 15;
  const selected = new Set(draft || []);
  const [openSlug, setOpenSlug] = useState(null);

  // Group by category for better scannability
  const groups = React.useMemo(() => {
    const g = new Map();
    for (const s of catalog) {
      const key = s.category || 'other';
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(s);
    }
    return Array.from(g.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [catalog]);

  // Localize category display labels via i18n with graceful fallback to a
  // Title-Cased slug for categories not yet present in the resource file.
  // Canonical category slugs remain unchanged in the API/data layer.
  const prettyCategoryFallback = (slug) => {
    if (!slug) return t('provider.profile.vip.categoryOther');
    return slug
      .split('_').join(' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };
  const categoryLabel = (slug) => t(`provider.profile.vip.categories.${slug || 'other'}`, {
    defaultValue: prettyCategoryFallback(slug),
  });

  return (
    <Card
      className="border border-slate-200/60 shadow-sm"
      data-testid="vip-services-card"
    >
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3
            className="text-sm font-semibold text-[#122033] font-[Manrope]"
            data-testid="vip-services-title"
          >
            {t('provider.profile.vip.title')}
          </h3>
          <Button
            size="sm"
            onClick={onSave}
            disabled={!dirty || saving}
            className="h-8 text-xs"
            data-testid="vip-services-save"
          >
            {saving
              ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              : <Save className="h-3.5 w-3.5 mr-1" />}
            {saving ? t('provider.profile.vip.saving') : t('provider.profile.vip.save')}
          </Button>
        </div>
        <p
          className="text-[11px] text-slate-500"
          data-testid="vip-services-helper"
        >
          {t('provider.profile.vip.helper', { max, count: (draft || []).length })}
        </p>

        {catalog.length === 0 ? (
          <p className="text-[12px] text-slate-400" data-testid="vip-services-empty">
            {t('provider.profile.vip.empty')}
          </p>
        ) : (
          <div className="space-y-3" data-testid="vip-services-groups">
            {groups.map(([cat, items]) => (
              <div key={cat} data-testid={`vip-services-group-${cat}`}>
                <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">
                  {categoryLabel(cat)}
                </p>
                <div className="flex flex-wrap gap-2">
                  {items.map(svc => {
                    const isSelected = selected.has(svc.slug);
                    const disabled = !isSelected && (draft || []).length >= max;
                    // Localized service label with graceful fallback to
                    // backend catalog `svc.name`. Canonical `svc.slug` never
                    // changes.
                    const svcLabel = t(`provider.profile.vip.services.${svc.slug}`, {
                      defaultValue: svc.name || svc.slug,
                    });
                    return (
                      <button
                        type="button"
                        key={svc.slug}
                        onClick={() => !disabled && onToggle(svc.slug)}
                        disabled={disabled}
                        data-testid={`vip-service-${svc.slug}`}
                        data-selected={isSelected ? 'true' : 'false'}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors ${
                          isSelected
                            ? 'bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-700'
                            : disabled
                            ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed'
                            : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {isSelected && <CheckCircle2 className="h-3 w-3" />}
                        <span>{svcLabel}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* P7Z-2C-B: per-slug description rows for SELECTED VIP services */}
            {(draft || []).length > 0 && (
              <div className="space-y-1.5 pt-1" data-testid="vip-services-descriptions">
                {(draft || []).map((slug) => {
                  const svc = catalog.find(c => c.slug === slug);
                  const display = t(`provider.profile.vip.services.${slug}`, {
                    defaultValue: svc?.name || slug,
                  });
                  const value = (detailsDraft || {})[slug] || '';
                  const isOpen = openSlug === slug || !!value;
                  return (
                    <div
                      key={slug}
                      className="rounded-md border border-slate-200 px-2 py-1.5 bg-slate-50/40"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-medium text-slate-700">{display}</span>
                        {!isOpen && (
                          <button
                            type="button"
                            className="text-[11px] text-indigo-600 hover:underline"
                            onClick={() => setOpenSlug(slug)}
                            data-testid={`vs-add-desc-${slug}`}
                          >
                            {t('provider.profile.vip.addDescription')}
                          </button>
                        )}
                      </div>
                      {isOpen && (
                        <textarea
                          rows={2}
                          maxLength={240}
                          placeholder={t('provider.profile.vip.descriptionPlaceholder')}
                          value={value}
                          onChange={(e) => onSetDescription(slug, e.target.value)}
                          className="mt-1 w-full text-[11px] p-1.5 border border-slate-200 rounded resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          data-testid={`vs-desc-input-${slug}`}
                        />
                      )}
                      {isOpen && (
                        <div className="text-[10px] text-slate-400 text-right">
                          {value.length}/240
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ===========================================================================
// Main page
// ===========================================================================
export default function ClinicProfileEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);

  // O82 — deep link from Integrations → iyzico: `?section=<editor>&return=<target>`
  // scrolls straight to the editor that owns the missing requirement and keeps
  // a reliable way back. Clinic scope is unchanged (always server-resolved).
  const focusSection = new URLSearchParams(location.search).get('section');
  const focusField = new URLSearchParams(location.search).get('field');
  const returnTarget = RETURN_TARGETS[new URLSearchParams(location.search).get('return')] || null;

  useEffect(() => {
    if (loading || !focusSection) return undefined;
    const el = document.getElementById(`profile-section-${focusSection}`);
    if (!el) return undefined;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.classList.add('ring-2', 'ring-teal-400', 'rounded-lg');
    // Land ON the outstanding field when the card knows which one it is.
    const field = focusField
      ? el.querySelector(`[data-testid="${CSS.escape(focusField)}"]`)
      : null;
    if (field) {
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      field.focus?.({ preventScroll: true });
    }
    const timer = setTimeout(
      () => el.classList.remove('ring-2', 'ring-teal-400'),
      2600,
    );
    return () => clearTimeout(timer);
  }, [loading, focusSection, focusField]);

  // Phase P1 — Identity (READ-ONLY)
  const [identity, setIdentity] = useState(null);

  // Phase P2 — Contact & Operations (EDITABLE)
  const [contact, setContact] = useState(null);
  const [contactDraft, setContactDraft] = useState(null);
  const [savingContact, setSavingContact] = useState(false);

  // Phase P3 — Billing & Bank Accounts
  const [billing, setBilling] = useState(null);
  const [billingDraft, setBillingDraft] = useState(null);
  const [savingBilling, setSavingBilling] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);

  // Phase P4 · Step 3 — Online Presence (reuses PUT /api/clinic/profile).
  const [onlinePresence, setOnlinePresence] = useState(null);
  const [presenceDraft, setPresenceDraft] = useState(null);
  const [savingPresence, setSavingPresence] = useState(false);

  // Phase P5 · Step 5A — Positioning Tags (providers SSOT).
  const [positioningTags, setPositioningTags] = useState(null); // {assigned, catalog, max, assigned_details}
  const [tagDraft, setTagDraft] = useState(null); // string[]
  // P7Z-2C-B: per-slug provider-authored descriptions (surf layer)
  const [tagDetailsDraft, setTagDetailsDraft] = useState({}); // { [slug]: string }
  const [savingTags, setSavingTags] = useState(false);

  // Phase P5 · Step 5B — VIP Services (providers SSOT).
  const [vipServices, setVipServices] = useState(null); // {assigned, catalog, max, assigned_details}
  const [vipDraft, setVipDraft] = useState(null); // string[]
  // P7Z-2C-B: per-slug provider-authored descriptions (surf layer)
  const [vipDetailsDraft, setVipDetailsDraft] = useState({}); // { [slug]: string }
  const [savingVip, setSavingVip] = useState(false);

  // F9 · shared refresh key — bumped by RepresentativesCard,
  // VerificationDocumentsCard, billing save, and bank-account CRUD so
  // PaymentReadinessCard re-fetches without a manual page refresh.
  const [readinessRefreshKey, setReadinessRefreshKey] = useState(0);

  // PROVIDER-IDENTITY-AMENDMENT (3B) — provider-side amendment state.
  const [amendments, setAmendments] = useState([]);
  const [amendmentDialogOpen, setAmendmentDialogOpen] = useState(false);
  const [submittingAmendment, setSubmittingAmendment] = useState(false);
  const latestAmendment = amendments[0] || null;
  const pendingAmendment = amendments.find(a => a?.status === 'pending') || null;

  const fetchAmendments = useCallback(async () => {
    try {
      const res = await api.get('/provider/identity-amendments');
      setAmendments(Array.isArray(res?.data?.amendments) ? res.data.amendments : []);
    } catch (e) {
      // non-fatal: profile still renders the approved identity without amendment context
      setAmendments([]);
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/clinic/profile');
      const data = res.data || {};
      setIdentity(data.identity || null);

      const effContact = data.contact || null;
      setContact(effContact);
      setContactDraft(effContact ? {
        public_contact_email: effContact.public_contact_email || '',
        public_contact_phone: effContact.public_contact_phone || '',
        opening_hours: effContact.opening_hours || '',
        address: effContact.address || '',
      } : null);

      const effBilling = data.billing || null;
      setBilling(effBilling);
      setBillingDraft(effBilling ? {
        invoice_legal_entity_name: effBilling.invoice_legal_entity_name || '',
        billing_address:           effBilling.billing_address || '',
        tax_id:                    effBilling.tax_id || '',
        // F9: individual-provider national identity number (only shown
        // when provider_type === 'doctor_practice'). Storage remains
        // canonical on clinic_profiles.
        personal_identity_number:  effBilling.personal_identity_number || '',
        provider_type:             effBilling.provider_type || '',
        // PSP sub-merchant KYC.
        legal_name:                effBilling.legal_name || '',
        legal_name_locked:         !!effBilling.legal_name_locked,
        contact_name:              effBilling.contact_name || '',
        contact_surname:           effBilling.contact_surname || '',
        tax_office:                effBilling.tax_office || '',
      } : null);

      setBankAccounts(Array.isArray(data.bank_accounts) ? data.bank_accounts : []);

      const effPresence = data.online_presence || null;
      const blank = { website: '', instagram: '', youtube: '', tiktok: '', facebook: '' };
      setOnlinePresence(effPresence || blank);
      setPresenceDraft({ ...blank, ...(effPresence || {}) });

      // P5-5A Positioning Tags
      const pt = data.positioning_tags || { assigned: [], catalog: [], max: 8 };
      setPositioningTags(pt);
      setTagDraft(Array.isArray(pt.assigned) ? [...pt.assigned] : []);
      // P7Z-2C-B: hydrate per-slug description map
      const ptdMap = {};
      (pt.assigned_details || []).forEach(d => {
        if (d?.slug && typeof d.description === 'string') ptdMap[d.slug] = d.description;
      });
      setTagDetailsDraft(ptdMap);

      // P5-5B VIP Services
      const vs = data.vip_services || { assigned: [], catalog: [], max: 15 };
      setVipServices(vs);
      setVipDraft(Array.isArray(vs.assigned) ? [...vs.assigned] : []);
      const vsdMap = {};
      (vs.assigned_details || []).forEach(d => {
        if (d?.slug && typeof d.description === 'string') vsdMap[d.slug] = d.description;
      });
      setVipDetailsDraft(vsdMap);
    } catch (e) {
      toast.error(t('provider.profile.parent.loadFailed', { message: e.message }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);
  useEffect(() => { fetchAmendments(); }, [fetchAmendments]);

  // PROVIDER-IDENTITY-AMENDMENT (3B) — submit a change request through the 3A
  // amendment API. Never writes the approved identity directly.
  const handleSubmitAmendment = async (payload) => {
    setSubmittingAmendment(true);
    try {
      await api.post('/provider/identity-amendments', payload);
      toast.success(t('provider.profile.billing.identity.toasts.submitted'));
      setAmendmentDialogOpen(false);
      await Promise.all([fetchAmendments(), fetchProfile()]);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409) toast.error(t('provider.profile.billing.identity.errors.alreadyPending'));
      else if (status === 422) toast.error(detail || t('provider.profile.billing.identity.validation.noChange'));
      else toast.error(t('provider.profile.billing.identity.errors.submitFailed', { message: detail || e.message }));
      // refresh in case a competing pending request already exists
      fetchAmendments();
    } finally {
      setSubmittingAmendment(false);
    }
  };

  // ── Contact save ────────────────────────────────────────────────────
  const contactChanged = useMemo(() => {
    if (!contact || !contactDraft) return false;
    return (
      (contactDraft.public_contact_email || '') !== (contact.public_contact_email || '') ||
      (contactDraft.public_contact_phone || '') !== (contact.public_contact_phone || '') ||
      (contactDraft.opening_hours || '')        !== (contact.opening_hours || '') ||
      (contactDraft.address || '')              !== (contact.address || '')
    );
  }, [contact, contactDraft]);

  const handleSaveContact = async () => {
    if (!contactDraft || !contactChanged) return;
    setSavingContact(true);
    try {
      const res = await api.put('/clinic/profile', {
        public_contact_email: contactDraft.public_contact_email || '',
        public_contact_phone: contactDraft.public_contact_phone || '',
        opening_hours:        contactDraft.opening_hours || '',
        address:              contactDraft.address || '',
      });
      const eff = res?.data?.contact || null;
      if (eff) {
        setContact(eff);
        setContactDraft({
          public_contact_email: eff.public_contact_email || '',
          public_contact_phone: eff.public_contact_phone || '',
          opening_hours:        eff.opening_hours || '',
          address:              eff.address || '',
        });
      }
      toast.success(t('provider.profile.contact.toasts.saved'));
      setReadinessRefreshKey((n) => n + 1); // address is a readiness fact.
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 401) toast.error(t('provider.profile.contact.errors.sessionExpired'));
      else if (status === 400) toast.error(detail || t('provider.profile.contact.errors.validation'));
      else if (status === 404) toast.error(t('provider.profile.contact.errors.notFound'));
      else toast.error(t('provider.profile.contact.errors.saveFailed', { message: detail || e.message }));
    } finally {
      setSavingContact(false);
    }
  };
  const setContactField = (field, value) => setContactDraft(d => ({ ...(d || {}), [field]: value }));

  // ── Billing save ────────────────────────────────────────────────────
  // PROVIDER-IDENTITY-AMENDMENT (3B/3A) — `invoice_legal_entity_name` is a
  // protected identity field: it is no longer editable here and is NOT part of
  // the billing dirty check or the PUT payload. It changes only through the
  // TreatX-reviewed amendment flow. Sending it would be rejected server-side
  // (409 IDENTITY_AMENDMENT_REQUIRED).
  const billingChanged = useMemo(() => {
    if (!billing || !billingDraft) return false;
    return (
      (billingDraft.billing_address || '')          !== (billing.billing_address || '') ||
      (billingDraft.tax_id || '')                   !== (billing.tax_id || '') ||
      (billingDraft.personal_identity_number || '') !== (billing.personal_identity_number || '') ||
      (billingDraft.contact_name || '')             !== (billing.contact_name || '') ||
      (billingDraft.contact_surname || '')          !== (billing.contact_surname || '') ||
      (billingDraft.tax_office || '')               !== (billing.tax_office || '') ||
      (!billingDraft.legal_name_locked
        && (billingDraft.legal_name || '') !== (billing.legal_name || ''))
    );
  }, [billing, billingDraft]);

  const handleSaveBilling = async () => {
    if (!billingDraft || !billingChanged) return;
    setSavingBilling(true);
    try {
      const res = await api.put('/clinic/billing', {
        billing_address:           billingDraft.billing_address || '',
        tax_id:                    billingDraft.tax_id || '',
        // F9: individual-provider identifier (only sent when populated).
        personal_identity_number:  billingDraft.personal_identity_number || '',
        // PSP sub-merchant KYC. `legal_name` is omitted once approved —
        // the server would reject it (409 IDENTITY_AMENDMENT_REQUIRED).
        ...(billingDraft.legal_name_locked ? {} : { legal_name: billingDraft.legal_name || '' }),
        contact_name:              billingDraft.contact_name || '',
        contact_surname:           billingDraft.contact_surname || '',
        tax_office:                billingDraft.tax_office || '',
      });
      const eff = res?.data?.billing || null;
      if (eff) {
        setBilling(eff);
        setBillingDraft({
          invoice_legal_entity_name: eff.invoice_legal_entity_name || '',
          billing_address:           eff.billing_address || '',
          tax_id:                    eff.tax_id || '',
          personal_identity_number:  eff.personal_identity_number || '',
          provider_type:             eff.provider_type || '',
          legal_name:                eff.legal_name || '',
          legal_name_locked:         !!eff.legal_name_locked,
          contact_name:              eff.contact_name || '',
          contact_surname:           eff.contact_surname || '',
          tax_office:                eff.tax_office || '',
        });
      }
      // F9: billing changes (tax_id / personal_identity_number) can
      // flip readiness — trigger PaymentReadinessCard refetch.
      setReadinessRefreshKey((n) => n + 1);
      toast.success(t('provider.profile.billing.toasts.saved'));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || t('provider.profile.billing.errors.saveFailed', { message: e.message }));
    } finally {
      setSavingBilling(false);
    }
  };
  const setBillingField = (field, value) => setBillingDraft(d => ({ ...(d || {}), [field]: value }));

  // ── Bank account CRUD ───────────────────────────────────────────────
  const handleBankCreate = async (payload) => {
    const res = await api.post('/clinic/bank-accounts', payload);
    setBankAccounts(res.data.bank_accounts || []);
    setReadinessRefreshKey((n) => n + 1); // F9 readiness may flip.
    toast.success(t('provider.profile.bank.toasts.added'));
  };
  const handleBankUpdate = async (accountId, payload) => {
    const res = await api.put(`/clinic/bank-accounts/${accountId}`, payload);
    setBankAccounts(res.data.bank_accounts || []);
    setReadinessRefreshKey((n) => n + 1);
    toast.success(t('provider.profile.bank.toasts.updated'));
  };
  const handleBankDelete = async (accountId) => {
    const res = await api.delete(`/clinic/bank-accounts/${accountId}`);
    setBankAccounts(res.data.bank_accounts || []);
    setReadinessRefreshKey((n) => n + 1);
    toast.success(t('provider.profile.bank.toasts.removed'));
  };
  const handleBankSetPrimary = async (accountId) => {
    const res = await api.put(`/clinic/bank-accounts/${accountId}`, { is_primary: true });
    setBankAccounts(res.data.bank_accounts || []);
    toast.success(t('provider.profile.bank.toasts.primaryUpdated'));
  };

  // ── Step 3 — Online Presence save (reuses PUT /api/clinic/profile) ───
  const PRESENCE_FIELDS = ['website', 'instagram', 'youtube', 'tiktok', 'facebook'];
  const URL_RE = /^https?:\/\/\S{3,}$/i;

  const presenceChanged = useMemo(() => {
    if (!onlinePresence || !presenceDraft) return false;
    return PRESENCE_FIELDS.some(
      f => (presenceDraft[f] || '').trim() !== (onlinePresence[f] || '').trim()
    );
  }, [onlinePresence, presenceDraft]);

  const [presenceErrors, setPresenceErrors] = useState({});

  const handleSavePresence = async () => {
    if (!presenceDraft || !presenceChanged) return;
    // Client-side URL validation for all non-empty values.
    const errs = {};
    const payload = {};
    for (const f of PRESENCE_FIELDS) {
      const cur = (onlinePresence?.[f] || '').trim();
      const next = (presenceDraft[f] || '').trim();
      if (next === cur) continue;
      if (next && !URL_RE.test(next)) {
        errs[f] = t('provider.profile.presence.urlInvalid');
      }
      payload[f] = next; // empty string clears
    }
    if (Object.keys(errs).length) {
      setPresenceErrors(errs);
      toast.error(Object.values(errs)[0]);
      return;
    }
    setPresenceErrors({});
    setSavingPresence(true);
    try {
      const res = await api.put('/clinic/profile', payload);
      const eff = res?.data?.online_presence || null;
      if (eff) {
        setOnlinePresence(eff);
        setPresenceDraft({
          website: eff.website || '', instagram: eff.instagram || '',
          youtube: eff.youtube || '', tiktok: eff.tiktok || '',
          facebook: eff.facebook || '',
        });
      }
      toast.success(t('provider.profile.presence.toasts.saved'));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 400) toast.error(detail || t('provider.profile.presence.errors.validation'));
      else toast.error(t('provider.profile.presence.errors.saveFailed', { message: detail || e.message }));
    } finally {
      setSavingPresence(false);
    }
  };
  const setPresenceField = (field, value) => {
    setPresenceDraft(d => ({ ...(d || {}), [field]: value }));
    setPresenceErrors(prev => ({ ...prev, [field]: undefined }));
  };

  // ── P5-5A Positioning Tags ──────────────────────────────────────────
  const tagsChanged = useMemo(() => {
    const base = positioningTags?.assigned || [];
    const cur = tagDraft || [];
    if (cur.length !== base.length) return true;
    const baseSet = new Set(base);
    if (cur.some(s => !baseSet.has(s))) return true;
    // P7Z-2C-B: also dirty when any per-slug description changed
    const baseDetMap = {};
    (positioningTags?.assigned_details || []).forEach(d => {
      if (d?.slug) baseDetMap[d.slug] = d.description || '';
    });
    const draftMap = tagDetailsDraft || {};
    const allSlugs = new Set([
      ...Object.keys(baseDetMap),
      ...cur.filter(s => (draftMap[s] || '').trim()),
    ]);
    for (const slug of allSlugs) {
      const a = (baseDetMap[slug] || '').trim();
      const b = ((draftMap[slug] || '').trim());
      // ignore details for slugs that are no longer in cur (cascade)
      if (!cur.includes(slug) && !a) continue;
      if (a !== b) return true;
    }
    return false;
  }, [positioningTags, tagDraft, tagDetailsDraft]);

  const toggleTag = (slug) => {
    setTagDraft(curr => {
      const list = Array.isArray(curr) ? [...curr] : [];
      const idx = list.indexOf(slug);
      if (idx >= 0) { list.splice(idx, 1); return list; }
      const max = positioningTags?.max || 8;
      if (list.length >= max) return list;
      list.push(slug);
      return list;
    });
  };

  const setTagDescription = (slug, value) => {
    setTagDetailsDraft(prev => ({ ...prev, [slug]: value }));
  };

  const handleSaveTags = async () => {
    if (!tagsChanged) return;
    setSavingTags(true);
    try {
      const slugs = tagDraft || [];
      // Build tag_details — only for currently-selected slugs with non-empty trimmed text
      const tag_details = [];
      slugs.forEach(slug => {
        const desc = ((tagDetailsDraft || {})[slug] || '').trim();
        if (desc) tag_details.push({ slug, description: desc });
      });
      const res = await api.put('/clinic/profile/positioning-tags', {
        tags: slugs,
        tag_details,
      });
      const pt = res?.data?.positioning_tags;
      if (pt) {
        setPositioningTags(pt);
        setTagDraft(Array.isArray(pt.assigned) ? [...pt.assigned] : []);
        const ptdMap = {};
        (pt.assigned_details || []).forEach(d => {
          if (d?.slug && typeof d.description === 'string') ptdMap[d.slug] = d.description;
        });
        setTagDetailsDraft(ptdMap);
      }
      toast.success(t('provider.profile.positioning.toasts.saved'));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 400) toast.error(detail || t('provider.profile.positioning.errors.validation'));
      else if (status === 404) toast.error(t('provider.profile.positioning.errors.notFound'));
      else toast.error(t('provider.profile.positioning.errors.saveFailed', { message: detail || e.message }));
    } finally {
      setSavingTags(false);
    }
  };

  // ── P5-5B VIP Services ──────────────────────────────────────────────
  const vipChanged = useMemo(() => {
    const base = vipServices?.assigned || [];
    const cur = vipDraft || [];
    if (cur.length !== base.length) return true;
    const baseSet = new Set(base);
    if (cur.some(s => !baseSet.has(s))) return true;
    // P7Z-2C-B: also dirty when any per-slug description changed
    const baseDetMap = {};
    (vipServices?.assigned_details || []).forEach(d => {
      if (d?.slug) baseDetMap[d.slug] = d.description || '';
    });
    const draftMap = vipDetailsDraft || {};
    const allSlugs = new Set([
      ...Object.keys(baseDetMap),
      ...cur.filter(s => (draftMap[s] || '').trim()),
    ]);
    for (const slug of allSlugs) {
      const a = (baseDetMap[slug] || '').trim();
      const b = ((draftMap[slug] || '').trim());
      if (!cur.includes(slug) && !a) continue;
      if (a !== b) return true;
    }
    return false;
  }, [vipServices, vipDraft, vipDetailsDraft]);

  const toggleVipService = (slug) => {
    setVipDraft(curr => {
      const list = Array.isArray(curr) ? [...curr] : [];
      const idx = list.indexOf(slug);
      if (idx >= 0) { list.splice(idx, 1); return list; }
      const max = vipServices?.max || 15;
      if (list.length >= max) return list;
      list.push(slug);
      return list;
    });
  };

  const setVipDescription = (slug, value) => {
    setVipDetailsDraft(prev => ({ ...prev, [slug]: value }));
  };

  const handleSaveVip = async () => {
    if (!vipChanged) return;
    setSavingVip(true);
    try {
      const slugs = vipDraft || [];
      const service_details = [];
      slugs.forEach(slug => {
        const desc = ((vipDetailsDraft || {})[slug] || '').trim();
        if (desc) service_details.push({ slug, description: desc });
      });
      const res = await api.put('/clinic/profile/vip-services', {
        services: slugs,
        service_details,
      });
      const vs = res?.data?.vip_services;
      if (vs) {
        setVipServices(vs);
        setVipDraft(Array.isArray(vs.assigned) ? [...vs.assigned] : []);
        const vsdMap = {};
        (vs.assigned_details || []).forEach(d => {
          if (d?.slug && typeof d.description === 'string') vsdMap[d.slug] = d.description;
        });
        setVipDetailsDraft(vsdMap);
      }
      toast.success(t('provider.profile.vip.toasts.saved'));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 400) toast.error(detail || t('provider.profile.vip.errors.validation'));
      else if (status === 404) toast.error(t('provider.profile.vip.errors.notFound'));
      else toast.error(t('provider.profile.vip.errors.saveFailed', { message: detail || e.message }));
    } finally {
      setSavingVip(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="clinic-profile-editor">
      <header className="bg-[#122033] text-white border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="text-white/70 hover:text-white hover:bg-white/10"
              onClick={() => navigate(returnTarget || '/clinic/admin')}
              data-testid="back-to-clinic"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />{' '}
              {returnTarget
                ? t('provider.pspOnboarding.returnToIyzico')
                : t('provider.profile.header.dashboard')}
            </Button>
            <div className="h-5 w-px bg-white/20" />
            <h1
              className="text-lg font-semibold tracking-tight font-[Manrope]"
              data-testid="provider-profile-title"
            >
              {t('provider.profile.header.title')}
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {returnTarget && (
          <div
            className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-md border border-teal-200 bg-teal-50 px-3 py-2"
            data-testid="profile-return-banner"
          >
            <p className="text-[11px] text-teal-800 flex-1">
              {t('provider.pspOnboarding.returnBanner')}
            </p>
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs w-full sm:w-auto bg-white"
              onClick={() => navigate(returnTarget)}
              data-testid="profile-return-to-integrations"
            >
              {t('provider.pspOnboarding.returnToIyzico')}
            </Button>
          </div>
        )}
        {loading ? (
          <div className="space-y-3" data-testid="profile-loading">
            {[1, 2, 3].map(i => <div key={i} className="h-20 bg-slate-100 animate-pulse rounded-lg" />)}
          </div>
        ) : (
          <>
            {/* 1. Provider Details (READ-ONLY) */}
            {identity && (
              <div id="profile-section-identity" data-testid="profile-section-identity" className="scroll-mt-20">
                <IdentityCard identity={identity} />
              </div>
            )}

            {/* 2. Contact & Operations (EDITABLE) */}
            {contactDraft && (
              <div id="profile-section-contact" data-testid="profile-section-contact" className="scroll-mt-20">
                <ContactOperationsCard
                  draft={contactDraft}
                  onChange={setContactField}
                  onSave={handleSaveContact}
                  saving={savingContact}
                  dirty={contactChanged}
                />
              </div>
            )}

            {/* 3. Billing & Legal Information (EDITABLE) */}
            {billingDraft && (
              <div id="profile-section-billing" data-testid="profile-section-billing" className="scroll-mt-20">
                <BillingCard
                  draft={billingDraft}
                  onChange={setBillingField}
                  onSave={handleSaveBilling}
                  saving={savingBilling}
                  dirty={billingChanged}
                  currentBrand={identity?.brand_name || ''}
                  currentLegal={billing?.invoice_legal_entity_name || ''}
                  amendment={latestAmendment}
                  onRequestChange={() => setAmendmentDialogOpen(true)}
                />
              </div>
            )}

            {/* 4. Bank Accounts (CRUD) */}
            <div id="profile-section-bank" data-testid="profile-section-bank" className="scroll-mt-20">
              <BankAccountsCard
                accounts={bankAccounts}
                onCreate={handleBankCreate}
                onUpdate={handleBankUpdate}
                onDelete={handleBankDelete}
                onSetPrimary={handleBankSetPrimary}
              />
            </div>

            {/* TREATX-MULTICCY-P0 · Settlement accounts & currency capability */}
            <div id="profile-section-settlement" data-testid="profile-section-settlement" className="scroll-mt-20">
              <SettlementAccountsCard refreshKey={bankAccounts.length} />
            </div>

            {/* F9 · Authorised Representatives (F2 authority) */}
            <div id="profile-section-representatives" data-testid="profile-section-representatives" className="scroll-mt-20">
              <RepresentativesCard onProviderStateChanged={() => setReadinessRefreshKey((n) => n + 1)} />
            </div>

            {/* F9 · Verification Documents (F2 authority, post-approval) */}
            <div id="profile-section-documents" data-testid="profile-section-documents" className="scroll-mt-20">
              <VerificationDocumentsCard onProviderStateChanged={() => setReadinessRefreshKey((n) => n + 1)} pendingAmendmentId={pendingAmendment?.id} />
            </div>

            {/* F9 · Payment / PSP Readiness (read-only) */}
            <PaymentReadinessCard refreshKey={readinessRefreshKey} />

            {/* F9 · Payment account onboarding + PSP activation status */}
            <PaymentAccountOnboardingCard
              refreshKey={readinessRefreshKey}
              onProviderStateChanged={() => setReadinessRefreshKey((n) => n + 1)}
            />

            {/* 5. Online Presence (additive — reuses PUT /api/clinic/profile) */}
            {presenceDraft && (
              <OnlinePresenceCard
                draft={presenceDraft}
                errors={presenceErrors}
                onChange={setPresenceField}
                onSave={handleSavePresence}
                saving={savingPresence}
                dirty={presenceChanged}
              />
            )}

            {/* ─── Service Identity (P7Y-1: visual grouping only — no logic change) ─── */}
            {(positioningTags || vipServices) && (
              <section
                className="space-y-4 pt-4"
                aria-labelledby="service-identity-heading"
                data-testid="service-identity-section"
              >
                <div className="space-y-1">
                  <h2
                    id="service-identity-heading"
                    className="text-base font-semibold text-[#122033] font-[Manrope]"
                    data-testid="service-identity-title"
                  >
                    {t('provider.profile.serviceIdentity.title')}
                  </h2>
                  <p
                    className="text-[12px] text-slate-500 max-w-2xl"
                    data-testid="service-identity-helper"
                  >
                    {t('provider.profile.serviceIdentity.helper')}
                  </p>
                </div>

                {/* 6. Positioning Tags (P5-5A — PUT /api/clinic/profile/positioning-tags) */}
                {positioningTags && (
                  <PositioningTagsCard
                    data={positioningTags}
                    draft={tagDraft}
                    detailsDraft={tagDetailsDraft}
                    onToggle={toggleTag}
                    onSetDescription={setTagDescription}
                    onSave={handleSaveTags}
                    saving={savingTags}
                    dirty={tagsChanged}
                  />
                )}

                {/* 7. VIP Services (P5-5B — PUT /api/clinic/profile/vip-services) */}
                {vipServices && (
                  <VipServicesCard
                    data={vipServices}
                    draft={vipDraft}
                    detailsDraft={vipDetailsDraft}
                    onToggle={toggleVipService}
                    onSetDescription={setVipDescription}
                    onSave={handleSaveVip}
                    saving={savingVip}
                    dirty={vipChanged}
                  />
                )}

                {/* 8. TCPN S2-D — Provider-level Languages
                    Multi-select sourced from languages_master(active=true). */}
                <LanguagesCard />
              </section>
            )}

            {/* TREATX-PROVIDER-ACCOUNT-LIFECYCLE-01 — voluntary deactivation */}
            <DeactivateAccountCard />
          </>
        )}
      </main>

      {/* PROVIDER-IDENTITY-AMENDMENT (3B) — request dialog */}
      <IdentityAmendmentDialog
        open={amendmentDialogOpen}
        onOpenChange={setAmendmentDialogOpen}
        currentBrand={identity?.brand_name || ''}
        currentLegal={billing?.invoice_legal_entity_name || ''}
        onSubmit={handleSubmitAmendment}
        submitting={submittingAmendment}
      />
    </div>
  );
}

// TCPN S2-D — Languages multi-select editor card. Self-contained so the
// billing, banks, presence, positioning tags, and VIP services.

// TREATX-PROVIDER-ACCOUNT-LIFECYCLE-01 — self-serve voluntary deactivation.
// Requires current-password re-entry (verified server-side). On success the
// provider is logged out and the app routes to the public reactivation page.
function DeactivateAccountCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!password) return;
    setBusy(true);
    try {
      await api.post('/provider/account/deactivate', { password, reason: reason.trim() || null });
      toast.success(t('accountLifecycle.deactivateSuccess'));
      try { localStorage.removeItem('treatx_unified_user'); } catch { /* noop */ }
      try { await api.post('/auth/logout'); } catch { /* noop */ }
      setTimeout(() => navigate('/reactivate'), 700);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = (d && typeof d === 'object' && d.message) ? d.message
        : (typeof d === 'string' ? d : t('accountLifecycle.deactivateError'));
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-red-200" data-testid="deactivate-account-card">
      <CardContent className="py-5">
        <h2 className="text-base font-semibold text-red-700 font-[Manrope] mb-1">
          {t('accountLifecycle.dangerZone')}
        </h2>
        <div className="text-sm font-medium text-slate-800">{t('accountLifecycle.deactivateTitle')}</div>
        <p className="text-[12px] text-slate-500 max-w-2xl mt-1 mb-4">
          {t('accountLifecycle.deactivateDesc')}
        </p>
        <Button
          variant="outline"
          className="border-red-300 text-red-700 hover:bg-red-50"
          onClick={() => { setPassword(''); setReason(''); setOpen(true); }}
          data-testid="deactivate-account-btn"
          aria-label={t('accountLifecycle.deactivateBtn')}
        >
          {t('accountLifecycle.deactivateBtn')}
        </Button>
      </CardContent>

      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent data-testid="deactivate-account-dialog">
          <DialogHeader>
            <DialogTitle>{t('accountLifecycle.deactivateDialogTitle')}</DialogTitle>
            <DialogDescription>{t('accountLifecycle.deactivateDialogBody')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-slate-600">{t('accountLifecycle.passwordLabel')}</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('accountLifecycle.passwordPlaceholder')}
                data-testid="deactivate-password"
                autoComplete="current-password"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">{t('accountLifecycle.reasonLabel')}</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('accountLifecycle.reasonPlaceholder')}
                data-testid="deactivate-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}
                    data-testid="deactivate-cancel">
              {t('accountLifecycle.cancel')}
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={submit}
              disabled={busy || !password}
              data-testid="deactivate-confirm"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('accountLifecycle.confirmDeactivate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function LanguagesCard() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]); // [{slug, name}]
  const [persisted, setPersisted] = useState([]);
  const [draft, setDraft] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/clinic/languages');
        if (cancelled) return;
        const cat = Array.isArray(res?.data?.catalog) ? res.data.catalog : [];
        const cur = Array.isArray(res?.data?.languages) ? res.data.languages : [];
        setCatalog(cat);
        setPersisted(cur);
        setDraft(cur);
      } catch (e) {
        toast.error(t('provider.profile.languages.errors.loadFailed', { message: e.message }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [t]);

  const dirty = useMemo(() => {
    const a = [...persisted].sort();
    const b = [...draft].sort();
    if (a.length !== b.length) return true;
    return a.some((s, i) => s !== b[i]);
  }, [persisted, draft]);

  const toggle = (slug) => {
    if (typeof slug !== 'string' || slug !== slug.toLowerCase()) return;
    setDraft((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));
  };

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      // Client-side guard: lowercase + dedupe before sending.
      const cleaned = Array.from(new Set(draft.filter((s) => typeof s === 'string' && s.length).map((s) => s.toLowerCase())));
      const res = await api.put('/clinic/languages', { languages: cleaned });
      const eff = Array.isArray(res?.data?.languages) ? res.data.languages : cleaned;
      setPersisted(eff);
      setDraft(eff);
      toast.success(t('provider.profile.languages.toasts.saved'));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.response?.data?.error;
      if (status === 400) toast.error(detail || t('provider.profile.languages.errors.invalidList'));
      else toast.error(t('provider.profile.languages.errors.saveFailed', { message: detail || e.message }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="border border-slate-200/60 shadow-sm" data-testid="languages-card">
        <CardContent className="py-4">
          <p className="text-sm text-slate-400">{t('provider.profile.languages.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-slate-200/60 shadow-sm" data-testid="languages-card">
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#122033] font-[Manrope]" data-testid="languages-card-title">
            {t('provider.profile.languages.title')}
          </h3>
          <Button
            size="sm"
            onClick={save}
            disabled={!dirty || saving}
            data-testid="languages-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            <span className="ml-1.5">{t('provider.profile.languages.save')}</span>
          </Button>
        </div>
        <p className="text-[12px] text-slate-500" data-testid="languages-helper">
          {t('provider.profile.languages.helper')}
        </p>
        {catalog.length === 0 ? (
          <p className="text-[12px] text-slate-400" data-testid="languages-empty">{t('provider.profile.languages.empty')}</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="languages-options">
            {catalog.map((row) => {
              const selected = draft.includes(row.slug);
              // Localized language display label with graceful fallback to
              // backend catalog `row.name`. Canonical `row.slug` never changes.
              const langLabel = t(`provider.profile.languages.names.${row.slug}`, {
                defaultValue: row.name || row.slug,
              });
              return (
                <button
                  type="button"
                  key={row.slug}
                  onClick={() => toggle(row.slug)}
                  className={
                    'inline-flex items-center px-2 py-1 text-xs rounded-full border transition ' +
                    (selected
                      ? 'bg-teal-50 text-teal-700 border-teal-300'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')
                  }
                  data-testid={`languages-toggle-${row.slug}`}
                  data-selected={selected ? 'true' : 'false'}
                >
                  {selected ? <CheckCircle2 className="h-3 w-3 mr-1" /> : null}
                  {langLabel}
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}



// ===========================================================================
// F9 · Authorised Representatives (F2 authority via /api/clinic/representatives)
// ===========================================================================
// Canonical role identifiers — must remain in sync with backend enums.
// Display labels come from i18n (provider.profile.reps.roles.<id>).
const REPRESENTATIVE_ROLE_IDS = [
  'primary_contact',
  'billing_contact',
  'clinical_contact',
  'compliance_contact',
];

const EMPTY_REP_FORM = {
  full_name: '',
  email: '',
  phone: '',
  roles: ['primary_contact'],
  is_primary: true,
};

function RepresentativesCard({ onProviderStateChanged }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [reps, setReps] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_REP_FORM);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/clinic/representatives');
      setReps(Array.isArray(res?.data?.representatives) ? res.data.representatives : []);
    } catch (e) {
      toast.error(t('provider.profile.reps.errors.loadFailed', { message: e?.response?.data?.detail || e.message }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const activeReps = reps.filter((r) => r.is_active !== false);

  const toggleRole = (roleId) => {
    setForm((f) => {
      const has = f.roles.includes(roleId);
      const next = has ? f.roles.filter((r) => r !== roleId) : [...f.roles, roleId];
      return { ...f, roles: next.length ? next : [roleId] };
    });
  };

  const submitAdd = async () => {
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error(t('provider.profile.reps.errors.nameEmailRequired'));
      return;
    }
    setBusy(true);
    try {
      await api.post('/clinic/representatives', {
        full_name: form.full_name.trim(),
        email:     form.email.trim(),
        phone:     form.phone.trim() || null,
        roles:     form.roles,
        is_primary: !!form.is_primary,
      });
      setForm(EMPTY_REP_FORM);
      setAdding(false);
      await load();
      onProviderStateChanged?.();
      toast.success(t('provider.profile.reps.toasts.added'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('provider.profile.reps.errors.addFailed', { message: e.message }));
    } finally {
      setBusy(false);
    }
  };

  const promote = async (id) => {
    setBusy(true);
    try {
      await api.post(`/clinic/representatives/${id}/promote-primary`);
      await load();
      onProviderStateChanged?.();
      toast.success(t('provider.profile.reps.toasts.primaryUpdated'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('provider.profile.reps.errors.generic', { message: e.message }));
    } finally { setBusy(false); }
  };

  const deactivate = async (rep) => {
    let promoteTo = null;
    if (rep.is_primary) {
      const others = activeReps.filter((r) => r.id !== rep.id);
      if (!others.length) {
        toast.error(t('provider.profile.reps.errors.needAnotherBeforeRemove'));
        return;
      }
      promoteTo = others[0].id;
    }
    setBusy(true);
    try {
      await api.post(`/clinic/representatives/${rep.id}/deactivate`, {
        promote_to_primary_id: promoteTo,
      });
      await load();
      onProviderStateChanged?.();
      toast.success(t('provider.profile.reps.toasts.removed'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('provider.profile.reps.errors.generic', { message: e.message }));
    } finally { setBusy(false); }
  };

  const roleLabel = (rid) => t(`provider.profile.reps.roles.${rid}`, { defaultValue: rid });

  return (
    <Card className="border border-slate-200/60 shadow-sm" data-testid="representatives-card">
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#122033] font-[Manrope]" data-testid="representatives-title">
            {t('provider.profile.reps.title')}
          </h3>
          {!adding && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setForm(EMPTY_REP_FORM); setAdding(true); }}
              data-testid="representatives-add-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> {t('provider.profile.reps.add')}
            </Button>
          )}
        </div>
        <p className="text-[11px] text-slate-500" data-testid="representatives-helper">
          {t('provider.profile.reps.helper')}
        </p>

        {loading ? (
          <p className="text-xs text-slate-400">{t('provider.profile.reps.loading')}</p>
        ) : activeReps.length === 0 && !adding ? (
          <p className="text-xs text-slate-400" data-testid="representatives-empty">
            {t('provider.profile.reps.empty')}
          </p>
        ) : (
          <ul className="space-y-2" data-testid="representatives-list">
            {activeReps.map((r) => (
              <li
                key={r.id}
                className="flex items-start justify-between border border-slate-200 rounded-md px-3 py-2"
                data-testid={`representative-row-${r.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-800 truncate">{r.full_name}</p>
                    {r.is_primary && (
                      <Badge className="bg-teal-50 text-teal-700 border border-teal-200 text-[10px]">
                        {t('provider.profile.reps.primaryBadge')}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{r.email}{r.phone ? ` · ${r.phone}` : ''}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {(r.roles || []).map(roleLabel).join(' · ')}
                  </p>
                </div>
                <div className="flex items-center gap-1 ml-2 shrink-0">
                  {!r.is_primary && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-[11px]"
                      onClick={() => promote(r.id)}
                      disabled={busy}
                      data-testid={`representative-promote-${r.id}`}
                    >
                      {t('provider.profile.reps.makePrimary')}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-[11px] text-red-600"
                    onClick={() => deactivate(r)}
                    disabled={busy}
                    data-testid={`representative-remove-${r.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {adding && (
          <div
            className="border border-dashed border-slate-300 rounded-md p-3 space-y-2 bg-slate-50/60"
            data-testid="representative-add-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.profile.reps.form.fullName')}</Label>
                <Input
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="text-sm"
                  data-testid="representative-input-full-name"
                  maxLength={200}
                />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.profile.reps.form.email')}</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="text-sm"
                  data-testid="representative-input-email"
                  maxLength={254}
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.profile.reps.form.phone')}</Label>
                <PhoneInput
                  value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v || '' })}
                  testId="representative-input-phone"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.profile.reps.form.roles')}</Label>
                <div className="flex flex-wrap gap-1.5">
                  {REPRESENTATIVE_ROLE_IDS.map((rid) => {
                    const on = form.roles.includes(rid);
                    return (
                      <button
                        key={rid}
                        type="button"
                        onClick={() => toggleRole(rid)}
                        className={
                          'text-[11px] px-2 py-1 rounded-full border transition ' +
                          (on
                            ? 'bg-teal-50 text-teal-700 border-teal-300'
                            : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')
                        }
                        data-testid={`representative-role-toggle-${rid}`}
                        data-selected={on ? 'true' : 'false'}
                      >
                        {roleLabel(rid)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className="sm:col-span-2 inline-flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={!!form.is_primary}
                  onChange={(e) => setForm({ ...form, is_primary: e.target.checked })}
                  data-testid="representative-input-primary"
                />
                {t('provider.profile.reps.form.setPrimary')}
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setForm(EMPTY_REP_FORM); setAdding(false); }}
                disabled={busy}
                data-testid="representative-cancel"
              >
                {t('provider.profile.reps.form.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={submitAdd}
                disabled={busy}
                data-testid="representative-submit"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                {t('provider.profile.reps.form.save')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ===========================================================================
// F9 · Verification Documents (post-approval upload — F2 authority)
// ===========================================================================
function VerificationDocumentsCard({ onProviderStateChanged, pendingAmendmentId }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState([]);
  const [uploadType, setUploadType] = useState('');
  const [expiry, setExpiry] = useState('');
  const [uploading, setUploading] = useState(false);
  const [updateRow, setUpdateRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/clinic/verification-documents');
      setDocs(Array.isArray(res?.data?.documents) ? res.data.documents : []);
    } catch (e) {
      toast.error(t('provider.profile.verification.errors.loadFailed', { message: e?.response?.data?.detail || e.message }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const statusLabel = (s) => {
    const key = { uploaded: 'pendingReview', pending_review: 'pendingReview', pending: 'pendingReview', approved: 'approved', rejected: 'rejected', archived: 'archived' }[s] || 'pendingReview';
    return t(`provider.profile.verification.status.${key}`);
  };
  const statusClass = (s) => (s === 'approved'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : s === 'rejected'
      ? 'bg-rose-50 text-rose-700 border-rose-200'
      : 'bg-amber-50 text-amber-700 border-amber-200');

  // Group by document type: current = non-superseded (latest); rest = history.
  const groups = React.useMemo(() => {
    const byType = {};
    for (const d of docs) {
      const k = d.document_type_id || d.document_type || 'unknown';
      (byType[k] = byType[k] || []).push(d);
    }
    return Object.entries(byType).map(([type, rows]) => {
      const current = rows.find((r) => !r.is_superseded) || rows[0];
      const history = rows.filter((r) => r !== current);
      return { type, current, history };
    });
  }, [docs]);

  const doUpload = async ({ file, documentTypeId, expiryDate, attachAmendmentId }) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('provider.profile.verification.errors.fileTooLarge'));
      return false;
    }
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    const b64 = String(dataUrl).split(',')[1] || '';
    await api.post('/clinic/verification-documents', {
      document_type_id: documentTypeId,
      filename: file.name,
      file_data: b64,
      expiry_date: expiryDate || null,
      ...(attachAmendmentId ? { identity_amendment_id: attachAmendmentId } : {}),
    });
    await load();
    onProviderStateChanged?.();
    return true;
  };

  const onNewTypeFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!uploadType.trim()) {
      toast.error(t('provider.profile.verification.errors.chooseType'));
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const ok = await doUpload({ file, documentTypeId: uploadType.trim(), expiryDate: expiry.trim() });
      if (ok) {
        setUploadType('');
        setExpiry('');
        toast.success(t('provider.profile.verification.toasts.uploaded'));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('provider.profile.verification.errors.uploadFailed', { message: err.message }));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <Card className="border border-slate-200/60 shadow-sm" data-testid="verification-docs-card">
      <CardContent className="py-4 space-y-3">
        <h3 className="text-sm font-semibold text-[#122033] font-[Manrope]" data-testid="verification-docs-title">
          {t('provider.profile.verification.title')}
        </h3>
        <p className="text-[11px] text-slate-500" data-testid="verification-docs-helper">
          {t('provider.profile.verification.helper')}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="sm:col-span-1">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.profile.verification.docTypeLabel')}</Label>
            <Input
              value={uploadType}
              onChange={(e) => setUploadType(e.target.value)}
              placeholder={t('provider.profile.verification.docTypePlaceholder')}
              className="text-sm"
              data-testid="verification-doc-type-input"
              maxLength={100}
            />
          </div>
          <div className="sm:col-span-1">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.profile.verification.expiryLabel')}</Label>
            <Input
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              placeholder={t('provider.profile.verification.expiryPlaceholder')}
              className="text-sm"
              data-testid="verification-doc-expiry-input"
              maxLength={32}
            />
          </div>
          <div className="sm:col-span-1 flex items-end">
            <label
              htmlFor="verification-doc-file"
              className={
                'inline-flex items-center gap-1 text-xs px-3 h-9 rounded-md border cursor-pointer ' +
                (uploading ? 'opacity-60 pointer-events-none ' : '') +
                'bg-white hover:bg-slate-50 border-slate-200 text-slate-700 w-full justify-center'
              }
              data-testid="verification-doc-upload-label"
            >
              {uploading
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Upload className="h-3.5 w-3.5" />}
              <span>{uploading ? t('provider.profile.verification.uploading') : t('provider.profile.verification.uploadFile')}</span>
            </label>
            <input
              id="verification-doc-file"
              type="file"
              onChange={onNewTypeFile}
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
              data-testid="verification-doc-file-input"
            />
          </div>
        </div>

        {loading ? (
          <p className="text-xs text-slate-400">{t('provider.profile.verification.loading')}</p>
        ) : groups.length === 0 ? (
          <p className="text-xs text-slate-400" data-testid="verification-docs-empty">
            {t('provider.profile.verification.empty')}
          </p>
        ) : (
          <ul className="space-y-2" data-testid="verification-docs-list">
            {groups.map(({ type, current, history }) => {
              const linkedToPending = pendingAmendmentId
                && current.linked_entity_type === 'identity_amendment'
                && current.linked_entity_id === pendingAmendmentId;
              return (
                <li
                  key={type}
                  className="border border-slate-200 rounded-md px-3 py-2 text-xs space-y-1.5"
                  data-testid={`verification-doc-row-${current.document_id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 truncate">
                        {current.document_type_name || current.document_type || current.document_type_id || t('provider.profile.verification.documentFallback')}
                      </p>
                      <p className="text-slate-500 truncate">{current.file_name}</p>
                      {linkedToPending && (
                        <span className="inline-block mt-0.5 text-[10px] text-purple-600" data-testid="verification-doc-amendment-linked">
                          {t('provider.profile.verification.linkedToIdentityChange')}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full border ${statusClass(current.status)}`} data-testid="verification-doc-status">
                        {statusLabel(current.status)}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[11px]"
                        onClick={() => setUpdateRow(current)}
                        data-testid="verification-doc-update-btn"
                      >
                        {current.status === 'rejected'
                          ? t('provider.profile.verification.uploadNewDocument')
                          : t('provider.profile.verification.update')}
                      </Button>
                    </div>
                  </div>
                  {current.status === 'rejected' && current.review_comment && (
                    <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1" data-testid="verification-doc-reject-reason">
                      <span className="font-semibold">{t('provider.profile.verification.reasonLabel')}:</span> {current.review_comment}
                    </p>
                  )}
                  {history.length > 0 && (
                    <p className="text-[10px] text-slate-400" data-testid="verification-doc-history">
                      {t('provider.profile.verification.previousVersions', { count: history.length })}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      <UpdateDocumentDialog
        row={updateRow}
        open={!!updateRow}
        onOpenChange={(v) => { if (!v) setUpdateRow(null); }}
        pendingAmendmentId={pendingAmendmentId}
        onUpload={doUpload}
      />
    </Card>
  );
}

function UpdateDocumentDialog({ row, open, onOpenChange, pendingAmendmentId, onUpload }) {
  const { t } = useTranslation();
  const alreadyLinked = row && row.linked_entity_type === 'identity_amendment' && row.linked_entity_id === pendingAmendmentId;
  const [attach, setAttach] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef(null);

  useEffect(() => { if (open) setAttach(!!alreadyLinked); }, [open, alreadyLinked]);

  if (!row) return null;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const ok = await onUpload({
        file,
        documentTypeId: row.document_type_id,
        expiryDate: null,
        attachAmendmentId: (pendingAmendmentId && attach) ? pendingAmendmentId : null,
      });
      if (ok) {
        toast.success(t('provider.profile.verification.toasts.uploaded'));
        onOpenChange(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('provider.profile.verification.errors.uploadFailed', { message: err.message }));
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="verification-doc-update-dialog">
        <DialogHeader>
          <DialogTitle>{t('provider.profile.verification.updateDialogTitle')}</DialogTitle>
          <DialogDescription>
            {row.document_type_name || row.document_type || row.document_type_id}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {pendingAmendmentId && (
            <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid="verification-doc-attach-toggle">
              <input
                type="checkbox"
                checked={attach}
                onChange={(e) => setAttach(e.target.checked)}
                className="h-4 w-4"
                data-testid="verification-doc-attach-checkbox"
              />
              {t('provider.profile.verification.attachToIdentityChange')}
            </label>
          )}
          <input
            ref={fileRef}
            type="file"
            onChange={onFile}
            disabled={busy}
            accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
            className="block w-full text-xs"
            data-testid="verification-doc-update-file-input"
          />
          {busy && <p className="text-[11px] text-slate-400 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> {t('provider.profile.verification.uploading')}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="verification-doc-update-cancel">
            {t('provider.profile.verification.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ===========================================================================
// F9 · Payment / PSP Readiness (read-only)
// ===========================================================================
// Canonical outstanding-item identifiers → i18n key suffix. The colon-based
// item codes (e.g. "field:legal_name") arrive verbatim from the backend and
// remain the source of truth for API/state; only their DISPLAY chrome is
// localized. `document:<type_id>` and `field:provider_type_unmapped:<slug>`
// use dynamic-suffix branches with provider-authored `type_id` / `<slug>`
// values preserved verbatim after the localized prefix.
import { READINESS_ITEM_KEY_MAP, readinessItemLabel } from './readinessItemLabel';

// Canonical PSP status enum → i18n key suffix. Enum values (`pending`,
// `active`, `restricted`, `information_required`) remain the SoT for the
// API and any downstream logic; only display chrome is localized.
const PSP_STATUS_KEY_MAP = {
  information_required: 'information_required',
  pending:              'pending',
  active:               'active',
  restricted:           'restricted',
};

// ===========================================================================
// F9 · Payment account (PSP) onboarding + activation status
// GET  /api/clinic/iyzico/activation-status   (read-only canonical projection)
// POST /api/clinic/iyzico/onboard             (canonical IyzicoMarketplaceService)
//
// Every state below is DERIVED from canonical backend facts for display only —
// no status is invented or persisted here, and local KYC completeness is never
// presented as PSP approval.
// ===========================================================================
const ACTIVATION_BADGE_CLASS = {
  active:                   'bg-teal-50 text-teal-700 border border-teal-200',
  registered_not_chargeable: 'bg-sky-50 text-sky-700 border border-sky-200',
  ready_to_submit:          'bg-slate-50 text-slate-700 border border-slate-200',
  additional_info_required: 'bg-amber-50 text-amber-700 border border-amber-200',
  information_incomplete:   'bg-amber-50 text-amber-700 border border-amber-200',
  provider_rejected:        'bg-rose-50 text-rose-700 border border-rose-200',
  unavailable:              'bg-slate-50 text-slate-500 border border-slate-200',
};

export function PaymentAccountOnboardingCard({ refreshKey = 0, onProviderStateChanged, showFinanceLink = false, returnTo = null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [updateBusy, setUpdateBusy] = useState(false);
  const [notice, setNotice] = useState(null); // {tone, message}
  // What the SERVER said was still missing when it refused a submission. The
  // groups below are built from the canonical snapshot; when a refusal names
  // items the snapshot has not caught up with, showing "everything is done"
  // beside "mandatory information is missing" is a contradiction, so the
  // refusal's own items are listed too until the next successful read.
  const [refusedOutstanding, setRefusedOutstanding] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/clinic/iyzico/activation-status');
      setSnapshot(res?.data || null);
      // A freshly read state replaces an older notice; a stale one must not
      // keep contradicting what the card now shows.
      setNotice(null);
      setRefusedOutstanding([]);
    } catch (e) {
      setSnapshot(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const state = deriveActivationState(snapshot);
  const canStart = ['ready_to_submit', 'additional_info_required'].includes(state);

  // O82 — missing information becomes actionable: canonical blockers are
  // grouped onto the EXISTING Provider Profile editors that own them. A
  // server refusal that named its own outstanding items is merged in, so the
  // owner always sees WHICH information is missing rather than a bare
  // "mandatory information is missing" beside a card full of ticks.
  const { groups, unmapped } = useMemo(
    () => groupRequirements([
      ...(snapshot?.blockers || []),
      ...refusedOutstanding.filter((i) => !(snapshot?.blockers || []).includes(i)),
    ]),
    [snapshot, refusedOutstanding],
  );
  const firstIncompleteGroup = groups.find((g) => !g.complete);
  // Missing provider information is shown wherever it genuinely exists — even
  // after a submission — and completed groups stay visible so the provider can
  // see what is already done instead of an empty card.
  const showRequirementGroups = state !== 'unavailable' && groups.length > 0;
  const completeIsPrimary = !!firstIncompleteGroup && state !== 'active';
  // A submission cannot complete while TreatX-held information is missing —
  // never tell the provider that no action is needed in that case.
  const missingAfterSubmission = !!firstIncompleteGroup
    && !['information_incomplete', 'additional_info_required'].includes(state);
  const goToEditor = useCallback(
    (group) => navigate(profileEditPath(
      group.section,
      returnTo,
      requirementFieldTestId(group.items?.[0]),
    )),
    [navigate, returnTo],
  );

  const startOnboarding = async () => {
    setSubmitting(true);
    setNotice(null);
    try {
      const res = await api.post('/clinic/iyzico/onboard', {});
      setSnapshot(res?.data || null);
      setNotice({ tone: 'ok', message: t('provider.profile.pspOnboarding.notices.submitted') });
      toast.success(t('provider.profile.pspOnboarding.notices.submitted'));
      onProviderStateChanged?.();
    } catch (e) {
      // The API's error handler flattens a dict `detail` to the TOP LEVEL of
      // the body ({error, code, message, ...}). Older shapes still nest it.
      const data = e?.response?.data || {};
      const detail = (data.detail && typeof data.detail === 'object') ? data.detail : data;
      const code = detail?.code;
      if (code === 'marketplace_activation_pending') {
        // Genuine provider response (iyzico errorCode 2000): the merchant
        // account is not yet a Marketplace account. Never shown as approved.
        setNotice({ tone: 'warn', code, message: t('provider.profile.pspOnboarding.notices.marketplacePending') });
      } else if (code === 'provider_temporarily_unavailable') {
        setNotice({
          tone: 'warn', code,
          message: t('provider.profile.pspOnboarding.notices.temporarilyUnavailable'),
        });
      } else if (code === 'external_write_not_authorized') {
        // O83 — TreatX blocked its own egress. Never blame the Provider's data.
        setNotice({
          tone: 'warn', code,
          message: t('provider.profile.pspOnboarding.notices.submissionNotEnabled'),
        });
      } else if (code === 'requirements_outstanding') {
        const outs = Array.isArray(detail?.outstanding) ? detail.outstanding : [];
        // When the ONLY thing refused is the bank destination, nothing about the
        // company or the documents is missing — say so instead of asking the
        // provider to re-check paperwork that is already complete.
        const bankOnly = outs.length > 0
          && outs.every((o) => /^(banking|submerchant):/.test(String(o)));
        setNotice({
          tone: 'warn', code,
          message: t(`provider.profile.pspOnboarding.notices.${
            bankOnly ? 'bankDestinationBlocked' : 'requirementsOutstanding'}`),
        });
        // Name the EXACT items the server refused on, so the card cannot show
        // every group as complete beside a "mandatory information missing"
        // notice.
        setRefusedOutstanding(outs);
        // Readiness changed under us — keep the readiness card in step.
        onProviderStateChanged?.();
      } else if (code === 'submerchant_existence_unverified') {
        // O94/P0-1 — we could not confirm with iyzico whether an account
        // already exists, so nothing was created. Retryable, not the
        // clinic's fault.
        setNotice({
          tone: 'warn', code,
          message: t('provider.profile.pspOnboarding.notices.existenceUnverified'),
        });
      } else if (code === 'submerchant_exists_unbound') {
        setNotice({
          tone: 'warn', code,
          message: t('provider.profile.pspOnboarding.notices.existsUnbound'),
        });
      } else if (code === 'iyzico_not_configured') {
        setNotice({ tone: 'warn', message: t('provider.profile.pspOnboarding.notices.notConfigured') });
      } else {
        setNotice({
          tone: 'error',
          message: t('provider.profile.pspOnboarding.notices.failed'),
        });
      }
      if (detail?.activation_status) setSnapshot(detail.activation_status);
      else load();
    } finally {
      setSubmitting(false);
    }
  };

  // O93 — the submerchant is BOUND: registration already succeeded at iyzico.
  const submerchantBound = !!snapshot?.psp_account?.submerchant_bound;
  // O94 — deployment-level arming of `sub_merchant.update`. When it is off,
  // the action is disabled with an explanation instead of failing on click.
  const updateWriteEnabled =
    snapshot?.environment?.external_writes?.sub_merchant_update !== false;

  // Refresh. For a bound submerchant this runs the canonical O92 capability
  // reconciliation (internal, no iyzico call) and then re-reads the canonical
  // activation projection. For an unbound one it stays a pure read.
  const refreshStatus = async () => {
    if (!submerchantBound) { load(); return; }
    setSyncing(true);
    setNotice(null);
    try {
      const res = await api.post('/clinic/iyzico/activation/sync', {});
      setSnapshot(res?.data || null);
      setNotice({
        tone: res?.data?.restricted_by_authority ? 'warn' : 'ok',
        // O94/P0-2 — a restricted/suspended/closed account is NOT reactivated
        // by a refresh; say so instead of reporting "nothing has changed".
        message: t(res?.data?.restricted_by_authority
          ? 'provider.profile.pspOnboarding.notices.syncRestricted'
          : res?.data?.capability_changed
            ? 'provider.profile.pspOnboarding.notices.syncUpdated'
            : 'provider.profile.pspOnboarding.notices.syncUnchanged'),
      });
      onProviderStateChanged?.();
    } catch (e) {
      setNotice({ tone: 'error', message: t('provider.profile.pspOnboarding.notices.syncFailed') });
      load();
    } finally {
      setSyncing(false);
    }
  };

  // Documented retrieval: POST /onboarding/submerchant/detail at iyzico.
  const retrieveDetail = async () => {
    setDetailBusy(true);
    setNotice(null);
    try {
      const res = await api.post('/clinic/iyzico/submerchant/detail', {});
      setDetail(res?.data?.detail || {});
      setSnapshot((prev) => (res?.data ? { ...(prev || {}), ...res.data } : prev));
    } catch (e) {
      const data = e?.response?.data || {};
      const d = (data.detail && typeof data.detail === 'object') ? data.detail : data;
      setDetail(null);
      setNotice({
        tone: 'warn',
        message: d?.code === 'submerchant_not_bound'
          ? t('provider.profile.pspOnboarding.notices.detailNotBound')
          : t('provider.profile.pspOnboarding.notices.detailFailed'),
      });
    } finally {
      setDetailBusy(false);
    }
  };

  // Documented update: PUT /onboarding/submerchant. The payload is built
  // server-side from the canonical provider record — never re-entered here.
  const sendUpdate = async () => {
    setUpdateBusy(true);
    setNotice(null);
    try {
      const res = await api.post('/clinic/iyzico/submerchant/update', {});
      setSnapshot(res?.data || null);
      setNotice({ tone: 'ok', message: t('provider.profile.pspOnboarding.notices.updated') });
      toast.success(t('provider.profile.pspOnboarding.notices.updated'));
      onProviderStateChanged?.();
    } catch (e) {
      const data = e?.response?.data || {};
      const d = (data.detail && typeof data.detail === 'object') ? data.detail : data;
      const code = d?.code;
      const updOuts = Array.isArray(d?.outstanding) ? d.outstanding : [];
      const updBankOnly = updOuts.length > 0
        && updOuts.every((o) => /^(banking|submerchant):/.test(String(o)));
      const key = code === 'requirements_outstanding'
          ? (updBankOnly ? 'bankDestinationBlocked' : 'requirementsOutstanding')
        : code === 'external_write_not_authorized' ? 'submissionNotEnabled'
        : code === 'marketplace_activation_pending' ? 'marketplacePending'
        : code === 'provider_temporarily_unavailable' ? 'temporarilyUnavailable'
        : code === 'submerchant_type_mismatch' ? 'typeMismatch'
        : code === 'type_verification_unavailable' ? 'typeUnverified'
        : code === 'iyzico_update_unavailable' ? 'updateUnavailable'
        : 'updateFailed';
      if (code === 'requirements_outstanding') {
        setRefusedOutstanding(updOuts);
      }
      setNotice({ tone: key === 'updateFailed' ? 'error' : 'warn', code, message: t(`provider.profile.pspOnboarding.notices.${key}`) });
      if (d?.activation_status) setSnapshot(d.activation_status);
    } finally {
      setUpdateBusy(false);
    }
  };

  // Canonical environment: the ACCOUNT's stamp when it exists, otherwise the
  // environment this deployment executes against. Never defaulted.
  const pspEnvironment = snapshot?.psp_account?.psp_environment
    || snapshot?.environment?.psp_environment
    || null;

  return (
    <Card className="border border-slate-200/60 shadow-sm" data-testid="psp-onboarding-card">
      <CardContent className="py-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-[#122033] font-[Manrope] flex items-center gap-1.5 min-w-0" data-testid="psp-onboarding-title">
            <Landmark className="h-3.5 w-3.5 shrink-0 text-slate-500" />
            <span className="truncate">{t('provider.profile.pspOnboarding.title')}</span>
          </h3>
          <div className="flex flex-wrap items-center gap-1.5">
            {/* O78 P0-1 — the provider must always know WHICH PSP world this
                account belongs to. `null` is reported as unknown, never
                silently assumed to be sandbox. */}
            {snapshot && (
              <Badge
                className={`text-[10px] whitespace-normal text-left ${
                  pspEnvironment === 'live'
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    : pspEnvironment === 'sandbox'
                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                    : 'bg-slate-100 text-slate-600 border border-slate-200'
                }`}
                data-testid={`psp-environment-${pspEnvironment || 'unknown'}`}
                title={pspEnvironment === 'sandbox' ? t('provider.pspOnboarding.envSandboxHint') : undefined}
              >
                {pspEnvironment === 'live'
                  ? t('provider.pspOnboarding.envLive')
                  : pspEnvironment === 'sandbox'
                  ? t('provider.pspOnboarding.envSandbox')
                  : t('provider.pspOnboarding.envUnknown')}
              </Badge>
            )}
            <Badge
              className={`${ACTIVATION_BADGE_CLASS[state]} text-[10px] whitespace-normal text-left`}
              data-testid={`psp-activation-state-${state}`}
            >
              {t(`provider.profile.pspOnboarding.states.${state}`)}
            </Badge>
          </div>
        </div>
        <p className="text-[11px] text-slate-500" data-testid="psp-onboarding-helper">
          {t('provider.profile.pspOnboarding.helper')}
        </p>
        <p className="text-[11px] text-slate-500" data-testid="psp-what-is-this">
          {t('provider.profile.pspOnboarding.whatIsThis')}
        </p>

        {loading ? (
          <p className="text-xs text-slate-400">{t('provider.profile.readiness.checking')}</p>
        ) : !snapshot ? (
          <p className="text-xs text-slate-400" data-testid="psp-onboarding-unavailable">
            {t('provider.profile.readiness.unavailable')}
          </p>
        ) : (
          <>
            {/* A pending Marketplace activation is an account-level
                entitlement, not missing provider data — do not also tell the
                Provider to review their information. */}
            {!['marketplace_activation_pending', 'provider_temporarily_unavailable'].includes(notice?.code) && (
              <p className="text-xs text-slate-600" data-testid="psp-activation-explainer">
                {t(missingAfterSubmission
                  ? 'provider.profile.pspOnboarding.submittedButMissing'
                  : `provider.profile.pspOnboarding.explainers.${state}`)}
              </p>
            )}

            {showRequirementGroups && (
              <div className="space-y-1.5" data-testid="psp-requirement-groups">
                {groups.map((g) => (
                  <div
                    key={g.key}
                    className="flex flex-wrap items-start gap-2 rounded-md border border-slate-200 px-2.5 py-2"
                    data-testid={`psp-requirement-group-${g.key}`}
                  >
                    <div className="flex-1 min-w-[55%] space-y-0.5">
                      <p className="text-xs font-medium text-[#122033] flex items-center gap-1.5">
                        {g.complete
                          ? <CheckCircle2 className="h-3.5 w-3.5 text-teal-600 shrink-0" />
                          : <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />}
                        {t(`provider.profile.pspOnboarding.groups.${g.key}`)}
                      </p>
                      {!g.complete && (
                        // O95 — say WHO requires each group: iyzico's own
                        // registration prerequisites vs TreatX verification
                        // policy. A TreatX document is never presented as an
                        // iyzico requirement.
                        <p
                          className="text-[11px] text-slate-500 pl-5"
                          data-testid={`psp-group-owner-${g.key}`}
                        >
                          {t(`provider.profile.pspOnboarding.groupOwners.${
                            g.key === 'documents' ? 'treatx' : 'iyzico'
                          }`)}
                        </p>
                      )}
                      {g.complete ? (
                        <p className="text-[11px] text-teal-700 pl-5" data-testid={`psp-group-complete-${g.key}`}>
                          {t('provider.profile.pspOnboarding.groupDoneBadge')}
                        </p>
                      ) : (
                        <ul className="pl-5 space-y-0.5">
                          {g.items.map((item) => (
                            <li key={item} className="text-[11px] text-slate-600" data-testid={`psp-blocker-${item}`}>
                              {readinessItemLabel(item, t)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    {!g.complete && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs w-full sm:w-auto"
                        onClick={() => goToEditor(g)}
                        data-testid={`psp-group-edit-${g.key}`}
                      >
                        {t('provider.profile.pspOnboarding.editGroupAction')}
                      </Button>
                    )}
                  </div>
                ))}
                {unmapped.length > 0 && (
                  <div className="text-[11px] text-slate-500 pt-1" data-testid="psp-requirements-unmapped">
                    <p>{t('provider.profile.pspOnboarding.otherRequirements')}</p>
                    <ul className="pl-4 list-disc">
                      {unmapped.map((item) => (
                        <li key={item} data-testid={`psp-blocker-${item}`}>{readinessItemLabel(item, t)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {notice && (
              <p
                className={`text-[11px] rounded-md px-3 py-2 border ${
                  notice.tone === 'ok' ? 'text-teal-700 bg-teal-50 border-teal-200'
                  : notice.tone === 'warn' ? 'text-amber-700 bg-amber-50 border-amber-200'
                  : 'text-rose-700 bg-rose-50 border-rose-200'
                }`}
                data-testid="psp-onboarding-notice"
              >
                {notice.message}
              </p>
            )}

            {state === 'active' && (
              <p className="text-sm text-teal-700 flex items-center gap-2" data-testid="psp-ready-to-accept-payments">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                {t('provider.profile.pspOnboarding.readyToAccept')}
              </p>
            )}

            {/* O82 — one unmistakable primary action per state. A disabled
                submission button is never the focus of this card. */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 pt-1">
              {showRequirementGroups && firstIncompleteGroup && (
                <Button
                  size="sm"
                  variant={completeIsPrimary ? 'default' : 'outline'}
                  onClick={() => goToEditor(firstIncompleteGroup)}
                  className="h-9 text-xs w-full sm:w-auto"
                  data-testid="psp-primary-complete-info"
                >
                  <ArrowLeft className="h-3.5 w-3.5 mr-1 rotate-180" />
                  {t(state === 'additional_info_required'
                    ? 'provider.profile.pspOnboarding.updateAction'
                    : 'provider.profile.pspOnboarding.completeAction')}
                </Button>
              )}
              {!submerchantBound && (
                <Button
                  size="sm"
                  onClick={startOnboarding}
                  disabled={!canStart || submitting}
                  variant={canStart && !firstIncompleteGroup ? 'default' : 'outline'}
                  className="h-9 text-xs w-full sm:w-auto"
                  data-testid="psp-start-onboarding-btn"
                >
                  {submitting
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                    : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}
                  {t('provider.profile.pspOnboarding.submitAction')}
                </Button>
              )}
              <Button
                size="sm"
                variant={state === 'registered_not_chargeable' && !firstIncompleteGroup ? 'default' : 'outline'}
                onClick={refreshStatus}
                disabled={loading || submitting || syncing}
                className="h-9 text-xs w-full sm:w-auto"
                data-testid="psp-refresh-status-btn"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                {t(submerchantBound
                  ? 'provider.profile.pspOnboarding.syncAction'
                  : 'provider.profile.pspOnboarding.refreshAction')}
              </Button>
              {submerchantBound && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={retrieveDetail}
                    disabled={detailBusy}
                    className="h-9 text-xs w-full sm:w-auto"
                    data-testid="psp-retrieve-detail-btn"
                  >
                    {detailBusy
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                      : <Landmark className="h-3.5 w-3.5 mr-1" />}
                    {t('provider.profile.pspOnboarding.retrieveDetailAction')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={sendUpdate}
                    disabled={updateBusy || !!firstIncompleteGroup || !updateWriteEnabled}
                    className="h-9 text-xs w-full sm:w-auto"
                    data-testid="psp-send-update-btn"
                  >
                    {updateBusy
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                      : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}
                    {t('provider.profile.pspOnboarding.sendUpdateAction')}
                  </Button>
                </>
              )}
            </div>

            {submerchantBound && (
              <p className="text-[11px] text-slate-500" data-testid="psp-update-hint">
                {t(updateWriteEnabled
                  ? 'provider.profile.pspOnboarding.updateHint'
                  : 'provider.profile.pspOnboarding.updateDisabledHint')}
              </p>
            )}

            {detail && (
              <div
                className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 space-y-1"
                data-testid="psp-submerchant-detail"
              >
                <p className="text-[11px] font-semibold text-[#122033]">
                  {t('provider.profile.pspOnboarding.detailTitle')}
                </p>
                {[
                  ['sub_merchant_type', 'detailType'],
                  ['legal_company_title', 'detailLegalName'],
                  ['name', 'detailName'],
                  ['email', 'detailEmail'],
                  ['gsm_number', 'detailPhone'],
                  ['address', 'detailAddress'],
                  ['tax_office', 'detailTaxOffice'],
                  ['iban_masked', 'detailIban'],
                  ['sub_merchant_key_masked', 'detailKey'],
                ].map(([field, labelKey]) => (
                  <p
                    key={field}
                    className="text-[11px] text-slate-600 flex flex-wrap gap-1"
                    data-testid={`psp-detail-${field}`}
                  >
                    <span className="text-slate-500">
                      {t(`provider.profile.pspOnboarding.${labelKey}`)}:
                    </span>
                    <span className="font-medium break-all">{detail[field] || '—'}</span>
                  </p>
                ))}
                <p className="text-[11px] text-slate-500" data-testid="psp-detail-note">
                  {t('provider.profile.pspOnboarding.detailNote')}
                </p>
              </div>
            )}

            <p className="text-[11px] text-slate-500" data-testid="psp-after-submit-note">
              {t('provider.profile.pspOnboarding.afterSubmit')}
            </p>

            {!canStart && state !== 'active' && !missingAfterSubmission && (
              <p className="text-[11px] text-slate-400 flex items-start gap-1" data-testid="psp-start-disabled-hint">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                {t(`provider.profile.pspOnboarding.disabledHints.${state}`)}
              </p>
            )}

            {/* O78 P1 / O82 — secondary: the EXISTING provider financial view
                (Accountant Workspace → Finance), never a new reporting module. */}
            {showFinanceLink && (
              <button
                type="button"
                onClick={() => navigate('/clinic/accountant/workspace?view=finance')}
                className="text-[11px] text-slate-500 underline underline-offset-2 hover:text-slate-700"
                data-testid="psp-open-finance-link"
              >
                {t('provider.pspOnboarding.financeLink')}
              </button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PaymentReadinessCard({ refreshKey = 0 }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);

  const readinessLabel = useCallback((item) => readinessItemLabel(item, t), [t]);

  const pspStatusLabel = (rawStatus) => {
    const key = PSP_STATUS_KEY_MAP[rawStatus];
    if (key) return t(`provider.profile.readiness.pspStatuses.${key}`);
    return rawStatus;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/clinic/payment-readiness');
      setSnapshot(res?.data || null);
    } catch (e) {
      // TREATX payment-readiness transient UX (RCA): during a deploy/restart
      // this GET can receive an upstream Cloudflare/origin 5xx or a bare
      // network failure. Never surface raw proxy/upstream text to the Provider
      // — leave the card in its EXISTING neutral "unavailable" state
      // (snapshot === null). Genuine application-level errors (4xx) keep the
      // existing toast behaviour.
      const status = e?.response?.status;
      const isTransient = !e?.response || (typeof status === 'number' && status >= 500);
      if (isTransient) {
        setSnapshot(null);
      } else {
        toast.error(t('provider.profile.readiness.errors.loadFailed', { message: e?.response?.data?.detail || e.message }));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <Card className="border border-slate-200/60 shadow-sm" data-testid="payment-readiness-card">
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#122033] font-[Manrope]" data-testid="payment-readiness-title">
            {t('provider.profile.readiness.title')}
          </h3>
          {snapshot && (
            <Badge
              className={
                snapshot.ready
                  ? 'bg-teal-50 text-teal-700 border border-teal-200 text-[10px]'
                  : 'bg-amber-50 text-amber-700 border border-amber-200 text-[10px]'
              }
              data-testid="payment-readiness-flag"
            >
              {snapshot.ready ? t('provider.profile.readiness.readyBadge') : t('provider.profile.readiness.missingBadge')}
            </Badge>
          )}
        </div>
        <p className="text-[11px] text-slate-500" data-testid="payment-readiness-helper">
          {t('provider.profile.readiness.helper')}
        </p>

        {loading ? (
          <p className="text-xs text-slate-400">{t('provider.profile.readiness.checking')}</p>
        ) : !snapshot ? (
          <p className="text-xs text-slate-400">{t('provider.profile.readiness.unavailable')}</p>
        ) : snapshot.ready ? (
          <p className="text-sm text-teal-700 flex items-center gap-2" data-testid="payment-readiness-complete">
            <CheckCircle2 className="h-4 w-4" />
            {t('provider.profile.readiness.complete')}
          </p>
        ) : (
          <ul className="space-y-1" data-testid="payment-readiness-outstanding">
            {snapshot.outstanding.map((item) => (
              <li
                key={item}
                className="flex items-center gap-2 text-xs text-slate-700"
                data-testid={`payment-readiness-item-${item}`}
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" />
                <span>{readinessLabel(item)}</span>
                {readinessItemSection(item) && (
                  <span
                    className="text-[10px] text-slate-400"
                    data-testid={`payment-readiness-where-${item}`}
                  >
                    {t('provider.profile.readiness.fixIn')}{' '}
                    {t(`provider.profile.readiness.sections.${readinessItemSection(item)}`)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        {snapshot?.psp && (
          <div
            className="text-[11px] text-slate-500 border-t border-slate-100 pt-2"
            data-testid="payment-readiness-psp"
          >
            {t('provider.profile.readiness.pspStatusLabel')}{' '}
            <span className="text-slate-700 font-medium">
              {pspStatusLabel(snapshot.psp.status)}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
